import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useVehicles } from '../contexts/VehicleContext'
import { useAuth } from '../contexts/AuthContext'
import { useSettings } from '../contexts/SettingsContext'
import ResponsiveFleetMap, { PositionCreationRequest } from './ResponsiveFleetMap'
import SpeedChart from './SpeedChart'
import EventChart from './EventChart'
import { Vehicle } from '../types'
import { Search, Settings, BarChart3, Map, Activity, Moon, Sun, LogOut, User as UserIcon, Route, MapPin, Zap, Car, ChevronDown, ChevronUp, Eye, X, Layout, Monitor, Smartphone, AlertTriangle, Filter, Users, Shield, Truck, Target, CheckCircle, XCircle, Info, Globe, Columns, Maximize2, Lock, Briefcase, Layers, Radio, Building2, Cpu, Bell, Gauge } from 'lucide-react'
import CorridorManager, { CorridorCreationRequest } from './CorridorManager'
import FleetVehicleExplorer from './FleetVehicleExplorer'
import TabbedUserRoleManagement from './TabbedUserRoleManagement'
import MissionDashboard from './MissionDashboard'
import FleetStaffManagement from './FleetStaffManagement'
import FleetVehicleOperations, { type FleetVehicle } from './FleetVehicleOperations'
import VehicleDetailsModal, { type VehicleModalTab } from './VehicleDetailsModal'
import BaliseManagement from './BaliseManagement'
import CustomerManagement from './CustomerManagement'
import SystemMonitoring from './SystemMonitoring'
import SettingsCenter from './SettingsCenter'
import SiniBrand from './SiniBrand'
import WorkspaceSwitcher from './WorkspaceSwitcher'
import FleetAlertsPanel, { AlertCreationRequest } from './FleetAlertsPanel'
import ReportsPanel from './reports/ReportsPanel'
import { apiClient } from '../config/api'
import AssetPeripheralPicker, { type PickerPayload } from './AssetPeripheralPicker'
import AssetSmartLockWindow from './AssetSmartLockWindow'
import AssetDashcamWindow from './AssetDashcamWindow'
import AssetObdWindow from './AssetObdWindow'
import AssetTagsWindow from './AssetTagsWindow'
import AssetCockpitWindow from './AssetCockpitWindow'
import FixedWorkspaceNav from './workspace/FixedWorkspaceNav'
import CollapsibleSidePanel from './workspace/CollapsibleSidePanel'
import ContextDrawer from './workspace/ContextDrawer'
import { assetPeripheralService, type AssetPeripheral, peripheralCapabilityToWindowId } from '../services/assetPeripheralService'
import { DateRange, todayRange } from './CalendarModal'
import type { ControlCenterMission, ControlCenterRule, ControlCenterZone, ZoneType } from '../services/vehicleControlCenterTypes'

// Mac-style message notification component
interface MacMessageProps {
  message: string
  type: 'success' | 'error' | 'warning' | 'info'
  isVisible: boolean
  onClose: () => void
}

const MacMessage: React.FC<MacMessageProps> = ({ message, type, isVisible, onClose }) => {
  if (!isVisible) return null

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          icon: <CheckCircle size={18} />,
          iconColor: 'text-green-600',
          textColor: 'text-green-800'
        }
      case 'error':
        return {
          bg: 'bg-red-50',
          border: 'border-red-200',
          icon: <XCircle size={18} />,
          iconColor: 'text-red-600',
          textColor: 'text-red-800'
        }
      case 'warning':
        return {
          bg: 'bg-yellow-50',
          border: 'border-yellow-200',
          icon: <AlertTriangle size={18} />,
          iconColor: 'text-yellow-600',
          textColor: 'text-yellow-800'
        }
      case 'info':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          icon: <Info size={18} />,
          iconColor: 'text-blue-600',
          textColor: 'text-blue-800'
        }
    }
  }

  const styles = getTypeStyles()

  return (
    <div className="fixed top-4 right-4 z-[9999] animate-in slide-in-from-right duration-300">
      <div className={`
        ${styles.bg} ${styles.border} ${styles.textColor}
        border rounded-xl shadow-2xl backdrop-blur-xl p-4 min-w-80 max-w-96
      `}
      style={{
        backdropFilter: 'blur(20px) saturate(180%)',
        boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
      }}>
        <div className="flex items-start space-x-3">
          <div className={`${styles.iconColor} flex-shrink-0`}>
            {styles.icon}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium leading-relaxed">
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  )
}

// Mac-style confirmation dialog component
interface MacConfirmProps {
  message: string
  isVisible: boolean
  onConfirm: () => void
  onCancel: () => void
}

const MacConfirm: React.FC<MacConfirmProps> = ({ message, isVisible, onConfirm, onCancel }) => {
  const { t } = useTranslation()
  if (!isVisible) return null

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-2xl border border-white/20 p-6 min-w-96 max-w-md mx-4"
           style={{
             backdropFilter: 'blur(20px) saturate(180%)',
             boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
           }}>
        <div className="text-center">
          <div className="mb-4 flex justify-center">
            <AlertTriangle size={48} className="text-gray-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4">
            {t('confirmation')}
          </h3>
          <p className="text-sm text-gray-600 mb-6 leading-relaxed">
            {message}
          </p>
          <div className="flex space-x-3">
            <button
              onClick={onCancel}
              className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
            >
              {t('cancel')}
            </button>
            <button
              onClick={onConfirm}
              className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors font-medium"
            >
              {t('delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// Window layout constants shared by every ResponsiveMacWindow
const WINDOW_MENU_BAR_H = 60
const WINDOW_EDGE_MARGIN = 10
const WINDOW_TITLE_GRIP = 120 // px of title bar that must always stay reachable

// Cascade slots: each window instance gets a stable slot so windows that
// share the default centered placement never open exactly on top of each other.
let windowCascadeCounter = 0

// Responsive Mac-style window component with proper scrolling
interface ResponsiveMacWindowProps {
  id: string
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  isVisible: boolean
  isMinimized: boolean
  onClose: (e?: React.MouseEvent) => void
  onMinimize: (e?: React.MouseEvent) => void
  onRestore: (e?: React.MouseEvent) => void
  onFocus?: () => void
  zIndex?: number
  initialPosition?: { x: number, y: number }
  minWidth?: number
  minHeight?: number
  maxWidth?: number
  maxHeight?: number
  resizable?: boolean
  forcedPosition?: { x: number, y: number } | null
  forcedSize?: { width: number, height: number } | null
  darkMode?: boolean
}

const ResponsiveMacWindow: React.FC<ResponsiveMacWindowProps> = ({
  id,
  title,
  icon,
  children,
  isVisible,
  isMinimized,
  onClose,
  onMinimize,
  onRestore,
  onFocus,
  zIndex = 40,
  initialPosition = { x: 20, y: 80 },
  minWidth = 320,
  minHeight = 200,
  maxWidth = 1200,
  maxHeight = 800,
  resizable = true,
  forcedPosition = null,
  forcedSize = null,
  darkMode = false
}) => {
  const { t } = useTranslation()
  const [position, setPosition] = useState(initialPosition)
  const [size, setSize] = useState({ width: minWidth, height: minHeight })
  const [isDragging, setIsDragging] = useState(false)
  const [isResizing, setIsResizing] = useState(false)
  const [isMaximized, setIsMaximized] = useState(false)
  const [preMaxState, setPreMaxState] = useState<{ x: number; y: number; w: number; h: number } | null>(null)
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 })
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 })
  const windowRef = useRef<HTMLDivElement>(null)
  // Stable cascade slot per window instance (assigned once on mount)
  const cascadeSlotRef = useRef<number | null>(null)
  if (cascadeSlotRef.current === null) {
    cascadeSlotRef.current = windowCascadeCounter++
  }

  const toggleMaximize = () => {
    if (isMaximized && preMaxState) {
      setPosition({ x: preMaxState.x, y: preMaxState.y })
      setSize({ width: preMaxState.w, height: preMaxState.h })
      setIsMaximized(false)
    } else {
      setPreMaxState({ x: position.x, y: position.y, w: size.width, h: size.height })
      setPosition({ x: 0, y: WINDOW_MENU_BAR_H })
      setSize({ width: window.innerWidth, height: window.innerHeight - WINDOW_MENU_BAR_H })
      setIsMaximized(true)
    }
  }

  const hasForcedLayout = !!(forcedPosition && forcedSize)

  useEffect(() => {
    if (forcedPosition) setPosition(forcedPosition)
  }, [forcedPosition?.x, forcedPosition?.y])

  useEffect(() => {
    if (forcedSize) setSize(forcedSize)
  }, [forcedSize?.width, forcedSize?.height])

  // Generic responsive layout: every window gets a viewport-fraction target
  // size clamped between its minimum and the visible screen, so no window
  // can ever open larger than the viewport. Dashboard panels (map, vehicles,
  // speed, events) keep their curated placement; every other window opens
  // centered with a per-instance cascade offset so windows never fully
  // stack on top of each other.
  const computeAutoLayout = () => {
    const screenWidth = window.innerWidth
    const screenHeight = window.innerHeight
    const availW = Math.max(280, screenWidth - WINDOW_EDGE_MARGIN * 2)
    const availH = Math.max(180, screenHeight - WINDOW_MENU_BAR_H - WINDOW_EDGE_MARGIN * 2)

    // Viewport-fraction size targets per window role
    let fracW = 0.72
    let fracH = 0.78
    if (id === 'map') { fracW = 0.5; fracH = 0.7 }
    else if (id === 'vehicles') { fracW = 0.18; fracH = 0.48 }
    else if (id === 'speed' || id === 'events') { fracW = 0.27; fracH = 0.42 }
    else if (id === 'corridors') { fracW = 0.8; fracH = 0.82 }

    // Small screens: windows grow toward full width
    if (screenWidth < 768) { fracW = 1; fracH = Math.max(fracH, 0.6) }
    else if (screenWidth < 1100) { fracW = Math.min(1, fracW + 0.18) }

    const effMinW = Math.min(minWidth, availW)
    const effMinH = Math.min(minHeight, availH)
    const effMaxW = Math.min(maxWidth, availW)
    const effMaxH = Math.min(maxHeight, availH)
    const width = Math.round(Math.max(effMinW, Math.min(effMaxW, screenWidth * fracW)))
    const height = Math.round(Math.max(effMinH, Math.min(effMaxH, screenHeight * fracH)))

    let newX: number
    let newY: number
    const isDashboardPanel = ['map', 'vehicles', 'speed', 'events'].includes(id)
    if (isDashboardPanel && screenWidth >= 1200) {
      if (id === 'map') { newX = Math.max(20, (screenWidth - width) / 2); newY = 80 }
      else if (id === 'vehicles') { newX = 20; newY = 80 }
      else if (id === 'speed') { newX = screenWidth - width - 30; newY = 80 }
      else { newX = screenWidth - width - 30; newY = 350 }
    } else if (isDashboardPanel && screenWidth >= 768) {
      if (id === 'map') { newX = 20; newY = 80 }
      else if (id === 'vehicles') { newX = screenWidth - width - 30; newY = 80 }
      else if (id === 'speed') { newX = 20; newY = screenHeight - height - 30 }
      else { newX = screenWidth - width - 30; newY = screenHeight - height - 30 }
    } else {
      // Centered with cascade offset so overlapping windows stay distinguishable
      const slot = cascadeSlotRef.current ?? 0
      const offset = (slot % 7) * 28
      newX = (screenWidth - width) / 2 - 84 + offset
      newY = (screenHeight - height) / 2 - 56 + (slot % 5) * 24
    }

    // Final clamp: fully on-screen, below the menu bar
    const x = Math.max(WINDOW_EDGE_MARGIN, Math.min(newX, screenWidth - width - WINDOW_EDGE_MARGIN))
    const y = Math.max(WINDOW_MENU_BAR_H + WINDOW_EDGE_MARGIN, Math.min(newY, screenHeight - height - WINDOW_EDGE_MARGIN))
    return { width, height, x, y }
  }

  // Auto-layout runs once on mount (skipped when layout is forced by auto-tile)
  useEffect(() => {
    if (hasForcedLayout) return
    const layout = computeAutoLayout()
    setSize({ width: layout.width, height: layout.height })
    setPosition({ x: layout.x, y: layout.y })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  // On browser resize: re-clamp (never reset) so user-arranged windows stay
  // reachable but keep their manual position/size when possible.
  useEffect(() => {
    const reclamp = () => {
      const screenWidth = window.innerWidth
      const screenHeight = window.innerHeight
      if (isMaximized) {
        setPosition({ x: 0, y: WINDOW_MENU_BAR_H })
        setSize({ width: screenWidth, height: screenHeight - WINDOW_MENU_BAR_H })
        return
      }
      if (hasForcedLayout) return
      setSize(prev => ({
        width: Math.min(prev.width, Math.max(280, screenWidth - WINDOW_EDGE_MARGIN * 2)),
        height: Math.min(prev.height, Math.max(180, screenHeight - WINDOW_MENU_BAR_H - WINDOW_EDGE_MARGIN))
      }))
      setPosition(prev => ({
        x: Math.max(WINDOW_EDGE_MARGIN, Math.min(prev.x, screenWidth - WINDOW_TITLE_GRIP)),
        y: Math.max(WINDOW_MENU_BAR_H, Math.min(prev.y, screenHeight - 48))
      }))
    }
    window.addEventListener('resize', reclamp)
    return () => window.removeEventListener('resize', reclamp)
  }, [hasForcedLayout, isMaximized])

  // Drag functionality
  const handleDragStart = (e: React.MouseEvent) => {
    // Don't start drag if clicking on buttons
    if ((e.target as HTMLElement).closest('button')) {
      return
    }
    
    if ((e.target as HTMLElement).closest('.drag-handle')) {
      setIsDragging(true)
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      })
    }
  }

  // Resize functionality
  const handleResizeStart = (e: React.MouseEvent) => {
    if (resizable) {
      setIsResizing(true)
      setResizeStart({
        x: e.clientX,
        y: e.clientY,
        width: size.width,
        height: size.height
      })
    }
  }

  // Mouse move handler
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        // Keep at least WINDOW_TITLE_GRIP px of the title bar on-screen so a
        // window can never be dragged out of reach, even when it is wider
        // than the viewport.
        const minX = -(size.width - WINDOW_TITLE_GRIP)
        const maxX = window.innerWidth - WINDOW_TITLE_GRIP
        const newX = Math.max(minX, Math.min(maxX, e.clientX - dragStart.x))
        const newY = Math.max(WINDOW_MENU_BAR_H, Math.min(window.innerHeight - 48, e.clientY - dragStart.y))
        setPosition({ x: newX, y: newY })
      } else if (isResizing) {
        // The corner can grow a window up to the screen. A low per-window
        // maxWidth used to stop the drag early (the vehicle list died at
        // 380 px). If the extra width would run off the right edge, the
        // window slides left so the new width stays reachable.
        const screenW = window.innerWidth
        const screenH = window.innerHeight
        const maxW = Math.max(minWidth, screenW - WINDOW_EDGE_MARGIN * 2)
        const maxH = Math.max(minHeight, screenH - WINDOW_MENU_BAR_H - WINDOW_EDGE_MARGIN)
        const newWidth = Math.max(minWidth, Math.min(maxW, resizeStart.width + (e.clientX - resizeStart.x)))
        const newHeight = Math.max(minHeight, Math.min(maxH, resizeStart.height + (e.clientY - resizeStart.y)))
        let nextX = position.x
        let nextY = position.y
        if (nextX + newWidth > screenW - WINDOW_EDGE_MARGIN) {
          nextX = Math.max(WINDOW_EDGE_MARGIN, screenW - WINDOW_EDGE_MARGIN - newWidth)
        }
        if (nextY + newHeight > screenH - WINDOW_EDGE_MARGIN) {
          nextY = Math.max(WINDOW_MENU_BAR_H, screenH - WINDOW_EDGE_MARGIN - newHeight)
        }
        setSize({ width: newWidth, height: newHeight })
        if (nextX !== position.x || nextY !== position.y) setPosition({ x: nextX, y: nextY })
      }
    }

    const handleMouseUp = () => {
      setIsDragging(false)
      setIsResizing(false)
    }

    if (isDragging || isResizing) {
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [isDragging, isResizing, dragStart, resizeStart, size, position, minWidth, minHeight, maxWidth, maxHeight])

  if (!isVisible) return null

  if (isMinimized) {
    return null
  }

  return (
    // IMPORTANT: no `transform` or `backdrop-filter` on this root element.
    // Both CSS properties turn the element into the containing block for
    // `position: fixed` descendants ? every modal opened inside a window
    // (`fixed inset-0`) would be positioned relative to the window and
    // clipped by its overflow-hidden instead of covering the viewport.
    // The glass-blur effect lives on an inner background layer instead.
    <div
      ref={windowRef}
      className={`fixed rounded-2xl shadow-2xl overflow-hidden transition-colors duration-300 ${
        darkMode 
          ? 'bg-gray-800/95 border border-gray-700/40' 
          : 'bg-white/95 border border-slate-200/90'
      }`}
      style={{
        zIndex: zIndex,
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${size.width}px`,
        height: `${size.height}px`,
        transition: isDragging || isResizing ? 'none' : 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        boxShadow: isDragging 
          ? '0 25px 50px -12px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)' 
          : darkMode
            ? '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.15)'
            : '0 8px 32px -12px rgba(20, 40, 80, 0.18), 0 2px 8px rgba(20, 40, 80, 0.07)'
      }}
      onMouseDown={() => {
        if (onFocus) {
          onFocus()
        }
      }}
    >
      {/* Glass-blur background layer (safe here: fixed modals are not its descendants).
          Must be -z-10: the root is a stacking context (fixed + z-index), so a negative
          z-index paints this layer above the root's background but BELOW the in-flow
          content. Without it, an absolutely-positioned sibling paints on top of the
          static content and blurs the entire window body ("all data disappeared" bug). */}
      <div className="absolute inset-0 -z-10 backdrop-blur-xl pointer-events-none" aria-hidden="true" />
      {/* Mac-style title bar */}
      <div 
        className={`drag-handle flex items-center justify-between px-3 py-1.5 backdrop-blur-md cursor-move select-none ${
          darkMode ? 'border-b border-gray-600/30' : 'border-b border-gray-300/30'
        }`}
        onMouseDown={handleDragStart}
        onDoubleClick={toggleMaximize}
        style={{
          background: darkMode
            ? 'linear-gradient(180deg, rgba(55,65,81,0.6) 0%, rgba(31,41,55,0.5) 100%)'
            : 'linear-gradient(180deg, rgba(255,255,255,0.96) 0%, rgba(248,250,252,0.92) 100%)',
          backdropFilter: 'blur(12px) saturate(120%)',
          WebkitBackdropFilter: 'blur(12px) saturate(120%)'
        }}
      >
        {/* Window control buttons */}
        <div className="flex items-center space-x-1.5">
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onClose(e)
            }}
            className="w-2.5 h-2.5 bg-gradient-to-b from-red-400 to-red-500 rounded-full hover:from-red-500 hover:to-red-600 transition-all duration-200 shadow-sm border border-red-600/20 flex items-center justify-center cursor-pointer group"
            title={t('close')}
          >
            <X size={6} className="text-red-900/0 group-hover:text-red-900/80 transition-colors" />
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              onMinimize(e)
            }}
            className="w-2.5 h-2.5 bg-gradient-to-b from-yellow-400 to-yellow-500 rounded-full hover:from-yellow-500 hover:to-yellow-600 transition-all duration-200 shadow-sm border border-yellow-600/20 flex items-center justify-center cursor-pointer group"
            title={t('minimize')}
          >
            <div className="w-1 h-px bg-yellow-900/0 group-hover:bg-yellow-900/80 rounded-full transition-colors" />
          </button>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              toggleMaximize()
            }}
            className="w-2.5 h-2.5 bg-gradient-to-b from-green-400 to-green-500 rounded-full hover:from-green-500 hover:to-green-600 transition-all duration-200 shadow-sm border border-green-600/20 flex items-center justify-center cursor-pointer group"
            title={isMaximized ? t('restore') : t('maximize')}
          >
            <div className="w-1 h-1 border border-green-900/0 group-hover:border-green-900/80 rounded-sm transition-colors" />
          </button>
        </div>

        {/* Window title with icon */}
        <div className="flex-1 flex items-center justify-center space-x-2">
          <div className={darkMode ? 'text-gray-400' : 'text-gray-600'}>{icon}</div>
          <h3 className={`text-xs font-semibold truncate tracking-tight ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}>{title}</h3>
        </div>

        <div className="w-5" aria-hidden="true" />
      </div>

      {/* Scrollable content area: explicit height minus title bar, with a
          min-h-0/min-w-0 flex chain so the inner scroll container always
          gets a real height and can show its scrollbars. overflow-x-auto
          (not hidden) so wide tables scroll horizontally instead of being
          silently clipped. */}
      <div className={`flex flex-col min-h-0 ${darkMode ? 'bg-gray-800' : ''}`} style={{ height: `${size.height - 36}px` }}>
        <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-auto">
          {children}
        </div>
      </div>

      {/* Resize handle */}
      {resizable && (
        <div
          className="absolute bottom-0 right-0 w-5 h-5 cursor-nw-resize group"
          onMouseDown={handleResizeStart}
        >
          <div className="absolute bottom-1 right-1 w-3 h-3 opacity-40 group-hover:opacity-70 transition-opacity">
            <div className="absolute bottom-0 right-0 w-1 h-1 bg-gray-500 rounded-full"></div>
            <div className="absolute bottom-0 right-2 w-1 h-1 bg-gray-500 rounded-full"></div>
            <div className="absolute bottom-2 right-0 w-1 h-1 bg-gray-500 rounded-full"></div>
          </div>
        </div>
      )}
    </div>
  )
}

// Enhanced vehicle list component with advanced search and filters
const ResponsiveVehicleList: React.FC<{
  vehicles: Vehicle[]
  selectedVehicle: Vehicle | null
  onVehicleSelect: (vehicle: Vehicle) => void
}> = ({ vehicles, selectedVehicle, onVehicleSelect }) => {
  const { t } = useTranslation()
  const [searchTerm, setSearchTerm] = useState('')
  const [searchCriteria, setSearchCriteria] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [customerFilter, setCustomerFilter] = useState('all')
  const [showFilters, setShowFilters] = useState(false)

  // Get unique customers for filter dropdown
  const uniqueCustomers = Array.from(new Set(
    vehicles.map(v => v.customerInfo?.name).filter(Boolean)
  )).sort()

  // Get unique statuses for filter dropdown
  const uniqueStatuses = Array.from(new Set(
    vehicles.map(v => v.status)
  )).sort()

  const filteredVehicles = vehicles.filter(vehicle => {
    // Text search based on selected criteria
    let matchesSearch = true
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      switch (searchCriteria) {
        case 'name':
          matchesSearch = vehicle.name.toLowerCase().includes(term)
          break
        case 'plate':
          matchesSearch = vehicle.plateNumber?.toLowerCase().includes(term) || false
          break
        case 'customer':
          matchesSearch = vehicle.customerInfo?.name?.toLowerCase().includes(term) || false
          break
        case 'location':
          matchesSearch = `${vehicle.location.latitude},${vehicle.location.longitude}`.includes(term)
          break
        case 'all':
        default:
          matchesSearch = 
            vehicle.name.toLowerCase().includes(term) ||
            vehicle.plateNumber?.toLowerCase().includes(term) ||
            vehicle.customerInfo?.name?.toLowerCase().includes(term) ||
            `${vehicle.location.latitude},${vehicle.location.longitude}`.includes(term)
          break
      }
    }

    // Status filter
    const matchesStatus = statusFilter === 'all' || vehicle.status === statusFilter

    // Customer filter
    const matchesCustomer = customerFilter === 'all' || vehicle.customerInfo?.name === customerFilter

    return matchesSearch && matchesStatus && matchesCustomer
  })

  const clearAllFilters = () => {
    setSearchTerm('')
    setSearchCriteria('all')
    setStatusFilter('all')
    setCustomerFilter('all')
  }

  const activeFiltersCount = [
    searchTerm !== '',
    statusFilter !== 'all',
    customerFilter !== 'all'
  ].filter(Boolean).length

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'moving': return 'bg-green-500'
      case 'stopped': return 'bg-red-500'
      case 'idle': return 'bg-yellow-500'
      case 'maintenance': return 'bg-purple-500'
      default: return 'bg-gray-500'
    }
  }

  const getStatusText = (status: string) => {
    switch (status) {
      case 'moving': return 'En route'
      case 'stopped': return 'Arr�t�'
      case 'idle': return 'Inactif'
      case 'maintenance': return 'Maintenance'
      default: return 'Hors ligne'
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search bar */}
      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Search vehicles, customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Vehicle stats */}
      <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="text-gray-600">
            <span className="font-medium">Total:</span> {vehicles.length}
          </div>
          <div className="text-gray-600">
            <span className="font-medium">Filtered:</span> {filteredVehicles.length}
          </div>
        </div>
        <div className="flex items-center justify-between mt-2 text-xs">
          <div className="flex items-center space-x-3">
            <span className="flex items-center">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-1"></div>
              {vehicles.filter(v => v.status === 'moving').length} Moving
            </span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-red-500 rounded-full mr-1"></div>
              {vehicles.filter(v => v.status === 'stopped').length} Stopped
            </span>
            <span className="flex items-center">
              <div className="w-2 h-2 bg-gray-500 rounded-full mr-1"></div>
              {vehicles.filter(v => v.status === 'offline').length} Offline
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable vehicle list */}
      <div className="flex-1 overflow-y-auto">
        {filteredVehicles.length === 0 ? (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <div className="text-center">
              <Search size={24} className="mx-auto mb-2 text-gray-400" />
              <div className="text-sm">No vehicles found</div>
              <div className="text-xs text-gray-400 mt-1">Try adjusting your search</div>
            </div>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {filteredVehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                onClick={() => onVehicleSelect(vehicle)}
                className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border ${
                  selectedVehicle?.id === vehicle.id
                    ? 'bg-blue-50 border-blue-200 shadow-md ring-2 ring-blue-100'
                    : 'bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm'
                }`}
              >
                {/* Vehicle header */}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start space-x-3 flex-1 min-w-0">
                    <div className={`w-4 h-4 rounded-full mt-0.5 flex-shrink-0 ${getStatusColor(vehicle.status)}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 text-sm leading-tight mb-1 truncate">
                        {vehicle.name}
                      </div>
                      <div className="text-xs text-gray-600 mb-2">
                        {vehicle.identifierType || 'ID'}: {vehicle.plateNumber}
                      </div>
                      
                      {/* Context information */}
                      <div className="space-y-1">
                        {vehicle.customerInfo?.name && (
                          <div className="text-xs text-blue-600 flex items-center">
                            <span className="mr-1">??</span>
                            <span className="truncate">{vehicle.customerInfo.name}</span>
                          </div>
                        )}
                        {vehicle.containerInfo?.name && (
                          <div className="text-xs text-green-600 flex items-center">
                            <span className="mr-1">??</span>
                            <span className="truncate">{vehicle.containerInfo.name}</span>
                          </div>
                        )}
                        {vehicle.containerInfo?.bookingReference && (
                          <div className="text-xs text-purple-600 flex items-center">
                            <span className="truncate">{t('booking')}: {vehicle.containerInfo.bookingReference}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="text-right flex-shrink-0">
                    <div className="text-lg font-bold text-gray-900">{vehicle.speed}</div>
                    <div className="text-xs text-gray-500">km/h</div>
                    <div className={`text-xs px-2 py-1 rounded-full mt-1 ${
                      vehicle.status === 'moving' ? 'bg-green-100 text-green-800' :
                      vehicle.status === 'stopped' ? 'bg-red-100 text-red-800' :
                      vehicle.status === 'idle' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {getStatusText(vehicle.status)}
                    </div>
                  </div>
                </div>

                {/* Vehicle metrics */}
                <div className="grid grid-cols-3 gap-3 text-xs">
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="font-semibold text-gray-800">{vehicle.batteryLevel || 'N/A'}</div>
                    <div className="text-gray-500">Battery %</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="font-semibold text-gray-800">{vehicle.signalStrength || 'N/A'}</div>
                    <div className="text-gray-500">Signal</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <div className="font-semibold text-gray-800 text-xs">
                      {new Date(vehicle.lastUpdate).toLocaleDateString('en-GB')}
                    </div>
                    <div className="font-semibold text-gray-800">
                      {new Date(vehicle.lastUpdate).toLocaleTimeString('en-US', { 
                        hour12: true,
                        hour: 'numeric',
                        minute: '2-digit',
                        second: '2-digit'
                      })}
                    </div>
                    <div className="text-gray-500">Last Update</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer with selection info */}
      {selectedVehicle && (
        <div className="px-4 py-3 bg-blue-50 border-t border-blue-200">
          <div className="text-xs text-blue-800">
            <div className="font-semibold mb-1">Selected Vehicle</div>
            <div className="text-blue-700">{selectedVehicle.name}</div>
            <div className="text-blue-600 mt-1 flex items-center gap-1">
              <MapPin size={14} /> {selectedVehicle.location.latitude.toFixed(4)}, {selectedVehicle.location.longitude.toFixed(4)}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// Main Mac-style dashboard
const CompleteFleetDashboard: React.FC = () => {
  const { t, i18n } = useTranslation()
  const { state, selectVehicle } = useVehicles()
  const { user, logout, getRoleDisplayName, getRoleColor, can, isPlatformAdmin } = useAuth()
  const { effective: effectiveSettings, loading: settingsLoading } = useSettings()
  const settingsLayoutAppliedRef = useRef(false)
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef<HTMLDivElement>(null)
  const [timeRange, setTimeRange] = useState(2160)
  const [timelineHover, setTimelineHover] = useState<{ timestamp: string | null, position: { lat: number, lng: number } | null }>({ timestamp: null, position: null })
  const [isDarkMode, setIsDarkMode] = useState(false)
  
  // Shared date range state for all components
  // Opens on the last 24 hours: what matters on a live map is today's movement,
  // and a wider window is one click away in the calendar.
  const [sharedDateRange, setSharedDateRange] = useState<DateRange>(() => todayRange())
  const [corridorCreationRequest, setCorridorCreationRequest] = useState<CorridorCreationRequest | null>(null)
  const [alertCreationRequest, setAlertCreationRequest] = useState<AlertCreationRequest | null>(null)
  const [alertFocus, setAlertFocus] = useState<{ id: number, token: number } | null>(null)
  
  // Fleet Vehicles list filters drive what the map shows: the list reports
  // its filtered vehicle ids and the map only plots that set.
  const [mapVehicleIds, setMapVehicleIds] = useState<string[] | null>(null)
  const handleVehicleListFiltered = useCallback((ids: string[]) => {
    setMapVehicleIds(ids)
  }, [])
  const mapVehicles = React.useMemo(() => {
    if (mapVehicleIds == null) return state.vehicles
    const idSet = new Set(mapVehicleIds)
    return state.vehicles.filter(v => idSet.has(v.id))
  }, [state.vehicles, mapVehicleIds])

  // Window states
  const [windows, setWindows] = useState({
    map: { visible: true, minimized: false },
    vehicles: { visible: true, minimized: false },
    speed: { visible: false, minimized: false },
    events: { visible: false, minimized: false },
    corridors: { visible: false, minimized: false },
    users: { visible: false, minimized: false },
    missionControl: { visible: false, minimized: false },
    fleetStaff: { visible: false, minimized: false },
    vehicleOperations: { visible: false, minimized: false },
    vehicleControl: { visible: false, minimized: false },
    baliseManagement: { visible: false, minimized: false },
    customers: { visible: false, minimized: false },
    systemMonitoring: { visible: false, minimized: false },
    settings: { visible: false, minimized: false },
    fleetAlerts: { visible: false, minimized: false },
    reports: { visible: false, minimized: false },
    assetSmartLock: { visible: false, minimized: false },
    assetDashcam: { visible: false, minimized: false },
    assetObd: { visible: false, minimized: false },
    assetTags: { visible: false, minimized: false },
    assetCockpit: { visible: false, minimized: false }
  })

  // P8 ? bindings for asset-scoped peripheral windows. Each window can be
  // bound to a different (assetId, imei) pair when "Open all" tiles them
  // together, so we keep one binding slot per window.
  type AssetWindowBinding = { assetId: string, imei: string, deviceName?: string | null } | null
  const [assetWindowBindings, setAssetWindowBindings] = useState<{
    assetSmartLock: AssetWindowBinding
    assetDashcam: AssetWindowBinding
    assetObd: AssetWindowBinding
    assetTags: AssetWindowBinding
  }>({
    assetSmartLock: null,
    assetDashcam: null,
    assetObd: null,
    assetTags: null
  })
  const [cockpitBinding, setCockpitBinding] = useState<{ assetId: string, label?: string | null } | null>(null)
  const [vehicleControlBinding, setVehicleControlBinding] = useState<{
    vehicle: FleetVehicle
    tab: VehicleModalTab
    startEditing: boolean
  } | null>(null)

  // P8 ? picker modal state (shown when an asset has 2+ peripherals)
  const [picker, setPicker] = useState<{
    visible: boolean
    assetId: string
    peripherals: AssetPeripheral[]
  }>({ visible: false, assetId: '', peripherals: [] })

  // Track which windows have been opened at least once (so closed ones show a restore icon)
  const [everOpened, setEverOpened] = useState<Set<string>>(() => {
    const s = new Set<string>()
    Object.entries(windows).forEach(([id, w]) => { if (w.visible) s.add(id) })
    return s
  })

  // Taskbar states
  const [taskbarVisible, setTaskbarVisible] = useState(true)
  const [taskbarAutoHide, setTaskbarAutoHide] = useState(true)
  const [taskbarHovered, setTaskbarHovered] = useState(false)

  // Mac message state
  const [macMessage, setMacMessage] = useState<{
    message: string
    type: 'success' | 'error' | 'warning' | 'info'
    isVisible: boolean
  }>({
    message: '',
    type: 'info',
    isVisible: false
  })

  // Mac confirmation state
  const [macConfirm, setMacConfirm] = useState<{
    message: string
    isVisible: boolean
    onConfirm: (() => void) | null
    onCancel: (() => void) | null
  }>({
    message: '',
    isVisible: false,
    onConfirm: null,
    onCancel: null
  })

  // Window z-index management for bringing windows to front
  const initialWindowZIndex: {[key: string]: number} = {
    map: 40,
    vehicles: 41,
    speed: 42,
    events: 43,
    corridors: 44,
    users: 45,
    missionControl: 46,
    fleetStaff: 48,
    vehicleOperations: 49,
    vehicleControl: 50,
    baliseManagement: 51,
    customers: 52,
    systemMonitoring: 53,
    settings: 54,
    assetCockpit: 55,
    assetSmartLock: 56,
    assetDashcam: 57,
    assetObd: 58,
    assetTags: 59,
    fleetAlerts: 60,
    reports: 61
  }
  const [windowZIndex, setWindowZIndex] = useState<{[key: string]: number}>(initialWindowZIndex)

  // Two-mode workspace navigation
  const [showLayoutMenu, setShowLayoutMenu] = useState(false)
  type WindowMode = 'fixed' | 'mac'
  type PanelId = keyof typeof windows
  type FixedProfile = 'tracking' | 'operations' | 'administration'
  const [windowMode, setWindowMode] = useState<WindowMode>(() => {
    const stored = localStorage.getItem('fleet.workspace.mode')
    return stored === 'fixed' || stored === 'mac' ? stored : 'mac'
  })
  const [activeFixedPanel, setActiveFixedPanel] = useState<PanelId>(() => {
    const stored = localStorage.getItem('fleet.workspace.fixedPanel')
    // 'settings' is a contextual drawer now, never a primary fixed panel
    return stored && stored !== 'settings' && Object.prototype.hasOwnProperty.call(windows, stored) ? stored as PanelId : 'map'
  })
  const [fixedProfile, setFixedProfile] = useState<FixedProfile>(() => {
    const stored = localStorage.getItem('fleet.workspace.fixedProfile')
    return stored === 'operations' || stored === 'administration' ? stored : 'tracking'
  })
  const [fixedDrawer, setFixedDrawer] = useState<PanelId | null>(null)
  const [trackingPanelCollapsed, setTrackingPanelCollapsed] = useState<boolean>(() =>
    localStorage.getItem('fleet.workspace.trackingPanelCollapsed') === '1')
  const [mobileVehiclePanelOpen, setMobileVehiclePanelOpen] = useState(false)

  // Single source of truth for the theme: isDarkMode always mirrors the root
  // Tailwind `dark` class, whether it comes from settings or the menu toggle.
  const applyDarkMode = useCallback((dark: boolean) => {
    setIsDarkMode(dark)
    document.documentElement.classList.toggle('dark', dark)
  }, [])

  useEffect(() => {
    const theme = String(effectiveSettings['appearance.theme'] || 'light')
    const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
    applyDarkMode(theme === 'dark' || (theme === 'system' && prefersDark))
  }, [effectiveSettings])

  useEffect(() => {
    if (settingsLoading || settingsLayoutAppliedRef.current) return
    if (!localStorage.getItem('fleet.workspace.mode')) {
      const configured = String(effectiveSettings['workspace.window_mode'] || 'mac')
      setWindowMode(configured === 'dashboard' || configured === 'fixed' ? 'fixed' : 'mac')
    }
    settingsLayoutAppliedRef.current = true
  }, [effectiveSettings, settingsLoading])

  useEffect(() => {
    localStorage.setItem('fleet.workspace.mode', windowMode)
  }, [windowMode])

  useEffect(() => {
    localStorage.setItem('fleet.workspace.fixedPanel', activeFixedPanel)
  }, [activeFixedPanel])

  useEffect(() => {
    localStorage.setItem('fleet.workspace.fixedProfile', fixedProfile)
  }, [fixedProfile])

  useEffect(() => {
    localStorage.setItem('fleet.workspace.trackingPanelCollapsed', trackingPanelCollapsed ? '1' : '0')
  }, [trackingPanelCollapsed])

  useEffect(() => {
    document.title = String(effectiveSettings['branding.app_name'] || 'SiniFleet')
  }, [effectiveSettings['branding.app_name']])

  useEffect(() => {
    setWindows(current => ({
      ...current,
      fleetAlerts: effectiveSettings['features.show_alerts'] === false
        ? { ...current.fleetAlerts, visible: false }
        : current.fleetAlerts,
      reports: effectiveSettings['features.show_reports'] === false
        ? { ...current.reports, visible: false }
        : current.reports,
      missionControl: effectiveSettings['features.show_missions'] === false
        ? { ...current.missionControl, visible: false }
        : current.missionControl,
      fleetStaff: effectiveSettings['features.show_staff'] === false
        ? { ...current.fleetStaff, visible: false }
        : current.fleetStaff,
      corridors: effectiveSettings['features.show_geofences'] === false
        ? { ...current.corridors, visible: false }
        : current.corridors
    }))
  }, [
    effectiveSettings['features.show_alerts'],
    effectiveSettings['features.show_missions'],
    effectiveSettings['features.show_staff'],
    effectiveSettings['features.show_geofences']
  ])

  // Auto-hide taskbar after 3 seconds of inactivity
  useEffect(() => {
    if (!taskbarAutoHide) return

    let hideTimer: ReturnType<typeof setTimeout>
    
    if (!taskbarHovered) {
      hideTimer = setTimeout(() => {
        setTaskbarVisible(false)
      }, 3000)
    } else {
      setTaskbarVisible(true)
    }

    return () => {
      if (hideTimer) clearTimeout(hideTimer)
    }
  }, [taskbarHovered, taskbarAutoHide])

  // Close layout menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement
      
      // Don't close if clicking inside the dropdown or on layout buttons
      if (target.closest('.layout-dropdown') || target.closest('.layout-button')) {
        return
      }
      
      if (showLayoutMenu) {
        console.log('??? Clicking outside - closing layout menu')
        setShowLayoutMenu(false)
      }
    }

    if (showLayoutMenu) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showLayoutMenu])

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) setLangOpen(false)
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])


  const updateWindow = (windowId: keyof typeof windows, updates: Partial<{ visible: boolean, minimized: boolean }>) => {
    const featureSetting: Partial<Record<keyof typeof windows, string>> = {
      fleetAlerts: 'features.show_alerts',
      missionControl: 'features.show_missions',
      fleetStaff: 'features.show_staff',
      corridors: 'features.show_geofences',
      reports: 'features.show_reports'
    }
    const requiredSetting = featureSetting[windowId]
    if (updates.visible === true && requiredSetting && effectiveSettings[requiredSetting] === false) {
      showMacMessage(t('feature_disabled', { defaultValue: 'This module is disabled by application settings.' }), 'warning')
      return
    }
    setWindows(prev => ({
      ...prev,
      [windowId]: { ...prev[windowId], ...updates }
    }))
    // Opening or restoring a window must bring it to the front so an
    // "active" window is never hidden behind previously focused ones.
    if (updates.visible === true || updates.minimized === false) {
      bringToFront(windowId as string)
    }
  }

  // P8 ? open a single asset-scoped peripheral window.
  // Wired both as a direct call (from handleAssetSelected when the asset
  // has exactly one peripheral) and as a custom-event listener so
  // any descendant component (Fleet Map marker, Vehicle list row, the
  // picker modal itself) can request a window without prop drilling.
  const openAssetPeripheralWindow = React.useCallback((payload: PickerPayload) => {
    const wid = payload.windowId
    if (wid === 'fleetMap') {
      // GPS_TRACKER reuses the existing Fleet Map per the P8 plan.
      openModule('map')
      return
    }
    const knownIds = ['assetSmartLock', 'assetDashcam', 'assetObd', 'assetTags']
    if (!knownIds.includes(wid)) return
    setAssetWindowBindings((prev) => ({
      ...prev,
      [wid]: {
        assetId: payload.assetId,
        imei: payload.imei,
        deviceName: payload.peripheral.deviceName,
      },
    }))
    openModule(wid as PanelId)
  }, [windowMode])

  // P8 ? central asset-selection router. Called from any path that picks
  // an asset (Fleet Map marker, Vehicle list row, deep links). Applies
  // the 0/1/N rule to either open one window directly or surface the
  // peripheral picker modal.
  const handleAssetSelected = React.useCallback(async (assetId: string | number) => {
    try {
      const r = await assetPeripheralService.getAssetPeripherals(assetId)
      const list = r.peripherals || []
      if (list.length === 0) return
      if (list.length === 1) {
        const p = list[0]
        if (!p.imei) return
        openAssetPeripheralWindow({
          assetId: r.assetId,
          imei: p.imei,
          capability: p.capability,
          windowId: peripheralCapabilityToWindowId(p.capability),
          peripheral: p,
        })
        return
      }
      setPicker({ visible: true, assetId: r.assetId, peripherals: list })
    } catch (err) {
      // Soft failure ? we never want a JIMI lookup error to block the
      // existing asset detail flow. The console.warn is sufficient.
      // eslint-disable-next-line no-console
      console.warn('[handleAssetSelected] peripheral lookup failed:', err)
    }
  }, [openAssetPeripheralWindow])

  // Custom-event hooks so non-dashboard components can trigger the
  // asset-selection router and the per-window opener without owning the
  // window state.
  useEffect(() => {
    const onSelected = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      if (detail.assetId != null) handleAssetSelected(detail.assetId)
    }
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent).detail
      if (detail) openAssetPeripheralWindow(detail as PickerPayload)
    }
    window.addEventListener('fleet:asset-selected', onSelected as EventListener)
    window.addEventListener('fleet:open-jimi-asset-window', onOpen as EventListener)
    return () => {
      window.removeEventListener('fleet:asset-selected', onSelected as EventListener)
      window.removeEventListener('fleet:open-jimi-asset-window', onOpen as EventListener)
    }
  }, [handleAssetSelected, openAssetPeripheralWindow])

  // "Open in Fleet Map" from a balise modal (MapModal in Balise
  // Management): find the vehicle tracked by that balise, select it and
  // bring the Fleet Map window forward.
  useEffect(() => {
    const onBaliseSelected = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      const lockCode = String(detail.lockCode || '')
      if (lockCode) {
        const tracked = state.vehicles.find((v) => v.baliseCodes?.includes(lockCode))
        if (tracked) selectVehicle({ ...tracked })
      }
      openModule('map')
    }
    const onOpenCockpit = (e: Event) => {
      const detail = (e as CustomEvent).detail || {}
      const assetId = String(detail.assetId || detail.vehicleId || '')
      if (!assetId) return
      const vehicle = state.vehicles.find((v) => v.id === assetId)
      setCockpitBinding({
        assetId,
        label: vehicle?.name || vehicle?.plateNumber || detail.label || assetId,
      })
      openModule('assetCockpit')
      if (vehicle) selectVehicle({ ...vehicle })
    }
    window.addEventListener('fleet:balise-selected', onBaliseSelected as EventListener)
    window.addEventListener('fleet:open-asset-cockpit', onOpenCockpit as EventListener)
    return () => {
      window.removeEventListener('fleet:balise-selected', onBaliseSelected as EventListener)
      window.removeEventListener('fleet:open-asset-cockpit', onOpenCockpit as EventListener)
    }
  }, [state.vehicles, windowMode])

  // Bring window to front when clicked
  const MENU_BAR_HEIGHT = 32
  const bringToFront = (windowId: string) => {
    // Derive the next layer from the latest state. Focus actions often happen
    // back-to-back (for example Map -> "Geofence here"), and reading a
    // captured highestZIndex gave both windows the same z-index.
    setWindowZIndex(prev => {
      const next = Math.max(39, ...Object.values(prev)) + 1

      // Rebase before windows reach the modal layer (modals/overlays live at
      // z-[300]+, menu bar at z-[9999]).
      if (next < 250) return { ...prev, [windowId]: next }

      const sorted = Object.entries(prev).sort(([, a], [, b]) => a - b)
      const rebased: Record<string, number> = {}
      sorted.forEach(([k], i) => { rebased[k] = 40 + i })
      rebased[windowId] = 40 + sorted.length
      return rebased
    })
  }

  // Show Mac-style message (replaces alert())
  // IMPORTANT: wrapped in useCallback so the function identity is stable
  // across re-renders. This dashboard re-renders on every window-focus
  // (z-index bump), every auto-hide-taskbar tick and every drag tick.
  // Without useCallback every child window would see a new `showMessage`
  // prop on every parent render, invalidating their useCallbacks and
  // re-firing useEffects ? BaliseManagement was re-fetching `/api/bm/balises`
  // (50 + 500 rows!) every couple of seconds, which made action buttons
  // feel dead because the table was always mid-reload.
  const showMacMessage = useCallback((message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setMacMessage({
      message,
      type,
      isVisible: true
    })
    setTimeout(() => {
      setMacMessage(prev => ({ ...prev, isVisible: false }))
    }, 4000)
  }, [])

  // Live fleet-alert badge: poll active alert count every 60s (matches the
  // geofence engine cadence) and toast when new alerts arrive.
  const [activeAlertCount, setActiveAlertCount] = useState(0)
  const lastAlertCountRef = useRef<number | null>(null)
  useEffect(() => {
    let cancelled = false
    const poll = async () => {
      try {
        const res = await apiClient.get('/alerts?status=ACTIVE')
        if (cancelled) return
        const count = typeof res.data?.count === 'number' ? res.data.count : 0
        if (lastAlertCountRef.current != null && count > lastAlertCountRef.current) {
          showMacMessage(
            t('new_fleet_alerts', { defaultValue: '{{count}} new fleet alert(s) ? open the Alerts panel', count: count - lastAlertCountRef.current }),
            'warning'
          )
        }
        lastAlertCountRef.current = count
        setActiveAlertCount(count)
      } catch {
        // API unreachable or table missing: keep the last known badge
      }
    }
    poll()
    const timer = setInterval(poll, 60000)
    return () => { cancelled = true; clearInterval(timer) }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Show Mac-style confirmation (replaces confirm()) ? same useCallback
  // rationale as showMacMessage above.
  const showMacConfirm = useCallback((message: string): Promise<boolean> => {
    return new Promise((resolve) => {
      setMacConfirm({
        message,
        isVisible: true,
        onConfirm: () => {
          setMacConfirm(prev => ({ ...prev, isVisible: false }))
          resolve(true)
        },
        onCancel: () => {
          setMacConfirm(prev => ({ ...prev, isVisible: false }))
          resolve(false)
        }
      })
    })
  }, [])

  const capabilityForPanel = (panelId: PanelId): string | null => {
    const requirements: Partial<Record<PanelId, string>> = {
      map: 'tracking.read',
      speed: 'tracking.read',
      events: 'tracking.read',
      vehicles: 'vehicles.read',
      vehicleOperations: 'vehicles.read',
      vehicleControl: 'vehicles.read',
      assetCockpit: 'tracking.read',
      assetSmartLock: 'vehicles.manage',
      assetDashcam: 'tracking.read',
      assetObd: 'tracking.read',
      assetTags: 'tracking.read',
      missionControl: 'missions.read',
      corridors: 'vehicles.read',
      fleetStaff: 'staff.read',
      fleetAlerts: 'alerts.read',
      reports: 'tracking.read',
      customers: 'customers.read',
      baliseManagement: 'vehicles.read',
      users: 'users.manage',
      settings: 'settings.read'
    }
    return requirements[panelId] || null
  }

  const openModule = (panelId: PanelId) => {
    const featureSetting: Partial<Record<PanelId, string>> = {
      fleetAlerts: 'features.show_alerts',
      missionControl: 'features.show_missions',
      fleetStaff: 'features.show_staff',
      corridors: 'features.show_geofences',
      reports: 'features.show_reports'
    }
    const requiredCapability = capabilityForPanel(panelId)
    if (requiredCapability && !can(requiredCapability)) {
      showMacMessage(t('access_denied'), 'warning')
      return
    }
    if (panelId === 'systemMonitoring' && !isPlatformAdmin) {
      showMacMessage(t('access_denied'), 'warning')
      return
    }
    const requiredSetting = featureSetting[panelId]
    if (requiredSetting && effectiveSettings[requiredSetting] === false) {
      showMacMessage(t('feature_disabled', { defaultValue: 'This module is disabled by application settings.' }), 'warning')
      return
    }
    if (windowMode === 'fixed') {
      const trackingPanels: PanelId[] = ['map', 'vehicles', 'speed', 'events']
      const operationsPanels: PanelId[] = ['vehicleOperations', 'missionControl', 'corridors', 'fleetStaff', 'fleetAlerts', 'reports']
      const contextualPanels: PanelId[] = [
        'vehicleControl', 'assetCockpit', 'assetSmartLock', 'assetDashcam', 'assetObd', 'assetTags',
        // Settings is app-level, not a Management module: it overlays the
        // current profile as a drawer instead of hijacking the workspace.
        'settings'
      ]
      if (trackingPanels.includes(panelId)) {
        setFixedProfile('tracking')
        setActiveFixedPanel('map')
      } else if (contextualPanels.includes(panelId)) {
        setFixedDrawer(panelId)
      } else if (operationsPanels.includes(panelId)) {
        setFixedProfile('operations')
        setActiveFixedPanel(panelId)
      } else {
        setFixedProfile('administration')
        setActiveFixedPanel(panelId)
      }
    } else {
      updateWindow(panelId, { visible: true, minimized: false })
      bringToFront(panelId)
    }
    setShowLayoutMenu(false)
  }

  const openAlertFromMap = (alertId: number) => {
    setAlertFocus({ id: alertId, token: Date.now() })
    openModule('fleetAlerts')
  }

  const openAssetCockpit = useCallback((vehicle: Vehicle) => {
    const assetId = vehicle.id
    if (!assetId) return
    setCockpitBinding({ assetId, label: vehicle.name || vehicle.plateNumber })
    openModule('assetCockpit')
  }, [windowMode])

  const handleVehicleSelect = (vehicle: Vehicle) => {
    // Clone so re-clicking the already-selected vehicle still produces a new
    // reference: the map re-centers on the vehicle every time it is clicked,
    // even after the user panned away.
    selectVehicle({ ...vehicle })
    // Peripheral windows are opened only through explicit actions (map
    // marker menu, vehicle list button, fleet:asset-selected events) ?
    // never automatically on selection, which users found intrusive.
  }

  const openVehicleControlCenter = (
    vehicle: FleetVehicle,
    tab: VehicleModalTab = 'history',
    startEditing = false
  ) => {
    setVehicleControlBinding({ vehicle, tab, startEditing })
    openModule('vehicleControl')
  }

  const handleCreateFromVehiclePosition = (request: PositionCreationRequest) => {
    setCorridorCreationRequest({
      requestId: Date.now(),
      mode: request.mode,
      point: request.point,
      zoom: request.zoom,
      referenceTrajectory: request.referenceTrajectory,
      dateRange: request.dateRange,
      vehicle: {
        id: request.vehicle.id,
        source: request.vehicle.vehicleSource,
        assetType: request.vehicle.assetInfo?.type,
        trackingStatus: request.vehicle.trackingStatus,
        heading: request.vehicle.heading
      },
      vehicleLabel: request.vehicle.operationalIdentifier || request.vehicle.plateNumber || request.vehicle.name
    })
    openModule('corridors')
  }

  const handleCreateAlertForVehicle = (vehicle: Vehicle) => {
    const source = vehicle.vehicleSource
    const numericId = Number(String(vehicle.id).replace(/^(ASSET|CONTAINER)-/i, ''))
    if (!source || !['ASSET', 'CONTAINER'].includes(source) || !Number.isInteger(numericId)) {
      showMacMessage(
        t('assign_tracker_before_alert', { defaultValue: 'Assign this tracker to an asset or container before creating an alert.' }),
        'warning'
      )
      return
    }
    setAlertCreationRequest({
      requestId: Date.now(),
      target: {
        vehicle_source: source,
        vehicle_id: numericId,
        vehicle_code: vehicle.operationalIdentifier || vehicle.plateNumber || vehicle.name,
        customer_id: vehicle.customerInfo?.id ?? null
      }
    })
    openModule('fleetAlerts')
  }

  const handleCreateVehicleZone = (
    type: ZoneType,
    vehicle: {
      id: number
      vehicle_source: 'ASSET' | 'CONTAINER'
      vehicle_code: string
      custom_name?: string
      asset_type?: string
      tracking_status?: 'LIVE' | 'STALE' | 'OFFLINE' | 'NOT_TRACKED'
      last_latitude?: number
      last_longitude?: number
    }
  ) => {
    const lat = Number(vehicle.last_latitude)
    const lng = Number(vehicle.last_longitude)
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || (lat === 0 && lng === 0)) {
      showMacMessage(
        t('vcc_position_required', { defaultValue: 'This vehicle has no recorded position. Assign a reporting tracker before creating a zone from its location.' }),
        'warning'
      )
      return false
    }
    const endDate = new Date()
    const startDate = new Date(endDate.getTime() - 24 * 60 * 60 * 1000)
    setCorridorCreationRequest({
      requestId: Date.now(),
      mode: type === 'geofence' ? 'geofence' : 'trajectory',
      point: { lat, lng },
      zoom: 16,
      referenceTrajectory: [],
      dateRange: { startDate: startDate.toISOString(), endDate: endDate.toISOString() },
      vehicle: {
        id: String(vehicle.id),
        source: vehicle.vehicle_source,
        assetType: vehicle.asset_type,
        trackingStatus: vehicle.tracking_status
      },
      vehicleLabel: vehicle.custom_name || vehicle.vehicle_code
    })
    openModule('corridors')
    return true
  }

  const handleManageVehicleZone = (zone: ControlCenterZone, type: ZoneType) => {
    openModule('corridors')
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('fleet:corridor-focus', {
        detail: { zoneType: type, zoneId: String(zone.id) }
      }))
    }, 0)
  }

  const handleManageVehicleMission = (mission: ControlCenterMission) => {
    openModule('missionControl')
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('fleet:mission-focus', {
        detail: { missionId: mission.id }
      }))
    }, 0)
  }

  const openVehicleAlertRule = (
    vehicle: { vehicle_source: 'ASSET' | 'CONTAINER'; id: number; vehicle_code: string; customer_id?: number },
    rule?: ControlCenterRule
  ) => {
    setAlertCreationRequest({
      requestId: Date.now(),
      editingRuleId: rule?.id,
      target: {
        vehicle_source: vehicle.vehicle_source,
        vehicle_id: vehicle.id,
        vehicle_code: vehicle.vehicle_code,
        customer_id: vehicle.customer_id ?? null
      }
    })
    openModule('fleetAlerts')
  }

  // "Show on map" from Vehicle Management: open/focus the Fleet Map window
  // and select the matching map vehicle (map ids are "<SOURCE>-<id>").
  const handleShowVehicleOnMap = (v: { vehicle_source: 'ASSET' | 'CONTAINER', id: number, vehicle_code: string }) => {
    const mapId = `${v.vehicle_source}-${v.id}`
    const match = state.vehicles.find(mv => mv.id === mapId || mv.name === v.vehicle_code)
    if (match) selectVehicle(match)
    openModule('map')
    if (!match) {
      showMacMessage(t('vehicle_not_on_map', { defaultValue: '{{code}} has no position to display yet', code: v.vehicle_code }), 'info')
    }
  }

  // Clicking a vehicle's position in the list: select it and bring the map
  // forward, since the map is what the click is asking to see.
  const focusVehicleOnMap = (vehicle: Vehicle) => {
    selectVehicle({ ...vehicle })
    openModule('map')
  }

  // Handle timeline hover from charts
  const handleTimelineHover = (timestamp: string | null, position: { lat: number, lng: number } | null) => {
    setTimelineHover({ timestamp, position })
  }

  const panelMeta: Record<string, { icon: React.ReactNode, label: string, color: string }> = {
    map: { icon: <Map size={14} />, label: t('fleet_map'), color: '#10B981' },
    vehicles: { icon: <Car size={14} />, label: t('fleet_vehicles'), color: '#3B82F6' },
    speed: { icon: <Zap size={14} />, label: t('speed_analysis'), color: '#F59E0B' },
    events: { icon: <Activity size={14} />, label: t('events_analysis'), color: '#EF4444' },
    corridors: { icon: <Route size={14} />, label: t('corridor_management'), color: '#8B5CF6' },
    users: { icon: <Users size={14} />, label: t('user_role_management'), color: '#6366F1' },
    fleetStaff: { icon: <Shield size={14} />, label: t('fleet_staff_management'), color: '#7C3AED' },
    vehicleOperations: { icon: <Truck size={14} />, label: t('fleet_vehicle_operations'), color: '#22C55E' },
    vehicleControl: { icon: <Gauge size={14} />, label: t('vcc_title', { defaultValue: 'Vehicle Control Center' }), color: '#2563EB' },
    missionControl: { icon: <Target size={14} />, label: t('mission_control_system'), color: '#F97316' },
    baliseManagement: { icon: <Radio size={14} />, label: t('balise_management'), color: '#0891B2' },
    customers: { icon: <Building2 size={14} />, label: t('customer_management'), color: '#EC4899' },
    systemMonitoring: { icon: <Activity size={14} />, label: t('system_monitoring_title', { defaultValue: 'System monitoring' }), color: '#E11D48' },
    settings: { icon: <Settings size={14} />, label: t('settings_center', { defaultValue: 'Settings Center' }), color: '#2563EB' },
    fleetAlerts: { icon: <AlertTriangle size={14} />, label: t('fleet_alerts_title', { defaultValue: 'Fleet Alerts' }), color: '#DC2626' },
    reports: { icon: <BarChart3 size={14} />, label: t('reports.windowTitle', { defaultValue: 'Reports' }), color: '#7C3AED' },
    assetSmartLock: { icon: <Lock size={14} />, label: t('asset_lock_default_name', { defaultValue: 'Smart Lock' }), color: '#D97706' },
    assetDashcam:   { icon: <Cpu size={14} />,  label: t('asset_dashcam_default_name', { defaultValue: 'AI Dashcam' }), color: '#9333EA' },
    assetObd:       { icon: <Cpu size={14} />,  label: t('asset_obd_default_name', { defaultValue: 'OBD Telemetry' }), color: '#0891B2' },
    assetTags:      { icon: <Cpu size={14} />,  label: t('asset_tags_default_name', { defaultValue: 'Paired Tags' }), color: '#DB2777' },
    assetCockpit:   { icon: <Gauge size={14} />, label: t('cockpit_title', { defaultValue: 'Vehicle cockpit' }), color: '#0F766E' },
  }

  const fixedNavigationGroups: Array<{ label: string, panels: PanelId[] }> = [
    { label: t('workspace_group_tracking'), panels: ['map', 'vehicles', 'speed', 'events'] },
    { label: t('workspace_group_operations'), panels: ['vehicleOperations', 'missionControl', 'corridors', 'fleetStaff', 'fleetAlerts', 'reports'] },
    { label: t('workspace_group_management'), panels: ['baliseManagement', 'customers', 'users', 'systemMonitoring'] }
  ]
  const isModuleAvailable = (panelId: PanelId) => {
    const requiredCapability = capabilityForPanel(panelId)
    if (requiredCapability && !can(requiredCapability)) return false
    if (panelId === 'systemMonitoring' && !isPlatformAdmin) return false
    const featureSetting: Partial<Record<PanelId, string>> = {
      fleetAlerts: 'features.show_alerts',
      missionControl: 'features.show_missions',
      fleetStaff: 'features.show_staff',
      corridors: 'features.show_geofences',
      reports: 'features.show_reports'
    }
    const requiredSetting = featureSetting[panelId]
    return !requiredSetting || effectiveSettings[requiredSetting] !== false
  }

  useEffect(() => {
    if (windowMode === 'fixed' && !isModuleAvailable(activeFixedPanel)) {
      setActiveFixedPanel('map')
    }
  }, [
    windowMode,
    activeFixedPanel,
    user?.role,
    user?.capabilities,
    isPlatformAdmin,
    effectiveSettings['features.show_alerts'],
    effectiveSettings['features.show_missions'],
    effectiveSettings['features.show_staff'],
    effectiveSettings['features.show_geofences'],
    effectiveSettings['features.show_reports']
  ])

  const renderPanelContent = (panelId: string) => {
    switch (panelId) {
      case 'map':
        return <ResponsiveFleetMap vehicles={mapVehicles} selectedVehicle={state.selectedVehicle} onVehicleSelect={handleVehicleSelect} timeRange={timeRange} timelineHover={timelineHover} sharedDateRange={sharedDateRange} onDateRangeChange={setSharedDateRange} onCreateFromPosition={handleCreateFromVehiclePosition} onCreateAlert={handleCreateAlertForVehicle} onOpenAlertDetail={openAlertFromMap} />
      case 'vehicles':
        return <FleetVehicleExplorer vehicles={state.vehicles} selectedVehicle={state.selectedVehicle} onVehicleSelect={handleVehicleSelect} onFilteredChange={handleVehicleListFiltered} onOpenCockpit={openAssetCockpit} onShowOnMap={focusVehicleOnMap} dateRange={sharedDateRange} />
      case 'speed':
        return <SpeedChart vehicle={state.selectedVehicle} timeRange={timeRange} onTimelineHover={handleTimelineHover} dateRange={sharedDateRange} />
      case 'events':
        return <EventChart vehicle={state.selectedVehicle} timeRange={timeRange} onTimelineHover={handleTimelineHover} dateRange={sharedDateRange} />
      case 'corridors':
        return <CorridorManager creationRequest={corridorCreationRequest} onCorridorSelect={(c) => console.log('Selected corridor:', c)} onCorridorsChange={(c) => console.log('Corridors updated:', c)} showMessage={showMacMessage} showConfirm={showMacConfirm} />
      case 'users':
        return <TabbedUserRoleManagement onUserSelect={(u) => console.log('Selected user:', u)} onUsersChange={(u) => console.log('Users updated:', u)} showMessage={showMacMessage} showConfirm={showMacConfirm} />
      case 'fleetStaff':
        return <FleetStaffManagement showMessage={showMacMessage} />
      case 'vehicleOperations':
        return <FleetVehicleOperations showMessage={showMacMessage} onShowOnMap={handleShowVehicleOnMap} onCreateVehicleZone={handleCreateVehicleZone} onManageVehicleZone={handleManageVehicleZone} onManageVehicleMission={handleManageVehicleMission} onCreateVehicleAlertRule={(vehicle) => openVehicleAlertRule(vehicle)} onEditVehicleAlertRule={(rule, vehicle) => openVehicleAlertRule(vehicle, rule)} onOpenVehicleControlCenter={openVehicleControlCenter} />
      case 'vehicleControl':
        return vehicleControlBinding ? (
          <VehicleDetailsModal
            embedded
            isOpen
            vehicle={vehicleControlBinding.vehicle}
            activeTab={vehicleControlBinding.tab}
            onTabChange={(tab) => setVehicleControlBinding(previous => previous ? { ...previous, tab, startEditing: false } : previous)}
            startEditing={vehicleControlBinding.startEditing}
            onClose={() => windowMode === 'fixed' ? setFixedDrawer(null) : updateWindow('vehicleControl', { visible: false })}
            onSuccess={() => showMacMessage(t('vehicle_updated', { defaultValue: 'Vehicle updated successfully' }), 'success')}
            onShowOnMap={handleShowVehicleOnMap}
            onCreateZone={handleCreateVehicleZone}
            onManageZone={handleManageVehicleZone}
            onManageMission={handleManageVehicleMission}
            onCreateAlertRule={(vehicle) => openVehicleAlertRule(vehicle)}
            onEditAlertRule={(rule, vehicle) => openVehicleAlertRule(vehicle, rule)}
            showMessage={showMacMessage}
          />
        ) : null
      case 'missionControl':
        return <MissionDashboard showMessage={showMacMessage} />
      case 'baliseManagement':
        return <BaliseManagement showMessage={showMacMessage} />
      case 'customers':
        return <CustomerManagement showMessage={showMacMessage} />
      case 'systemMonitoring':
        return <SystemMonitoring showMessage={showMacMessage} />
      case 'settings':
        return <SettingsCenter showMessage={showMacMessage} />
      case 'fleetAlerts':
        return <FleetAlertsPanel showMessage={showMacMessage} showConfirm={showMacConfirm} creationRequest={alertCreationRequest} focusAlert={alertFocus} />
      case 'reports':
        return <ReportsPanel showMessage={showMacMessage} showConfirm={showMacConfirm} />
      case 'assetSmartLock': {
        const b = assetWindowBindings.assetSmartLock
        return b ? <AssetSmartLockWindow assetId={b.assetId} imei={b.imei} deviceName={b.deviceName} showMessage={showMacMessage} />
                 : <div className="p-4 text-sm text-gray-500 italic">{t('asset_window_no_binding', { defaultValue: 'Select an asset with a smart-lock peripheral to populate this window.' })}</div>
      }
      case 'assetDashcam': {
        const b = assetWindowBindings.assetDashcam
        return b ? <AssetDashcamWindow assetId={b.assetId} imei={b.imei} deviceName={b.deviceName} showMessage={showMacMessage} />
                 : <div className="p-4 text-sm text-gray-500 italic">{t('asset_window_no_binding', { defaultValue: 'Select an asset with a dashcam peripheral to populate this window.' })}</div>
      }
      case 'assetObd': {
        const b = assetWindowBindings.assetObd
        return b ? <AssetObdWindow assetId={b.assetId} imei={b.imei} deviceName={b.deviceName} showMessage={showMacMessage} />
                 : <div className="p-4 text-sm text-gray-500 italic">{t('asset_window_no_binding', { defaultValue: 'Select an asset with an OBD peripheral to populate this window.' })}</div>
      }
      case 'assetTags': {
        const b = assetWindowBindings.assetTags
        return b ? <AssetTagsWindow assetId={b.assetId} imei={b.imei} deviceName={b.deviceName} showMessage={showMacMessage} />
                 : <div className="p-4 text-sm text-gray-500 italic">{t('asset_window_no_binding', { defaultValue: 'Select an asset with paired tags to populate this window.' })}</div>
      }
      case 'assetCockpit':
        return cockpitBinding ? (
          <AssetCockpitWindow
            assetId={cockpitBinding.assetId}
            vehicleLabel={cockpitBinding.label}
            showMessage={showMacMessage}
            onOpenMap={() => {
              openModule('map')
            }}
            onOpenCapability={(windowId, imei, deviceName) => {
              if (['assetSmartLock', 'assetDashcam', 'assetObd', 'assetTags'].includes(windowId)) {
                setAssetWindowBindings((prev) => ({
                  ...prev,
                  [windowId]: { assetId: cockpitBinding.assetId, imei, deviceName },
                }))
                openModule(windowId as PanelId)
              }
            }}
          />
        ) : (
          <div className="p-4 text-sm text-gray-500 italic">
            {t('cockpit_no_binding', { defaultValue: 'Select a vehicle and open its cockpit to see balises and captors.' })}
          </div>
        )
      default:
        return null
    }
  }

  return (
    <div className={`h-screen overflow-hidden relative flex flex-col transition-all duration-500 ${
      isDarkMode 
        ? 'dark bg-gray-900' 
        : 'bg-[#E9ECF1]'
    }`}>
      {/* Mac-style menu bar ? fixed + z-[9999] ensures it ALWAYS stays above every floating window */}
      <div className={`fixed top-0 left-0 right-0 px-5 py-2 shadow-sm z-[9999] transition-all duration-500 ${
        isDarkMode 
          ? 'bg-gray-900 border-b border-gray-700/60' 
          : 'bg-white border-b border-gray-200/60'
      }`}>
        {/* Same bar in both layouts: branding on the left, fleet counts and
            account controls on the right. The fixed workspace adds its own
            navigation rows underneath. */}
        <div className="flex items-center h-11 justify-between">
          {/* Left side - App branding */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-3">
              <div className={`h-11 px-1 rounded-lg flex items-center border overflow-hidden ${
                isDarkMode ? 'bg-white/95 border-white/20' : 'bg-white border-gray-200'
              }`}>
                <SiniBrand compact />
              </div>
              <span className={`hidden sm:inline font-semibold text-sm tracking-tight ${
                isDarkMode ? 'text-white' : 'text-gray-800'
              }`}>{String(effectiveSettings['branding.app_name'] || t('fleet_portal'))}</span>
            </div>
          </div>

          {/* Fleet status and controls */}
          <div className="flex items-center space-x-2.5 text-xs">
            <div className="items-center space-x-3 hidden xl:flex">
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-green-500 rounded-full shadow-[0_0_0_3px_rgba(34,197,94,.12)]"></div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{state.vehicles.filter(v => v.status === 'moving').length} {t('moving')}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{state.vehicles.filter(v => v.status === 'stopped').length} {t('stopped')}</span>
              </div>
              <div className="flex items-center space-x-1.5">
                <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                <span className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{state.vehicles.filter(v => v.status === 'offline').length} {t('offline')}</span>
              </div>
            </div>
            <span className={`hidden md:inline ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`} style={{ fontSize: '12px' }}>
              {new Date().toLocaleTimeString('fr-FR')}
            </span>

            <WorkspaceSwitcher darkMode={isDarkMode} compact />
            
            {/* User info */}
            {user && (
              <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-lg border text-xs ${
                isDarkMode
                  ? 'bg-gray-800/50 border-gray-700/40 text-gray-300'
                  : 'bg-gray-50 border-gray-200/60 text-gray-700'
              }`}>
                <UserIcon size={15} className={isDarkMode ? 'text-gray-400' : 'text-gray-500'} />
                <span className="hidden lg:inline font-medium">{user.fullName || user.username}</span>
                <span className={`hidden xl:inline text-[11px] ${getRoleColor()}`}>{getRoleDisplayName()}</span>
              </div>
            )}

            {/* Settings and System Monitoring are deliberately separate.
                Settings is role-aware for every user; Monitoring is admin-only
                operational health. */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => openModule('settings')}
                className={`p-2 rounded-lg transition-all duration-200 border ${
                  windows.settings.visible
                    ? 'bg-blue-100 text-blue-700 border-blue-200'
                    : isDarkMode
                      ? 'bg-gray-800/60 text-gray-300 border-gray-700 hover:bg-gray-700'
                      : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                }`}
                title={t('settings_center', { defaultValue: 'Settings Center' })}
                aria-label={t('settings_center', { defaultValue: 'Settings Center' })}
              >
                <Settings size={16} />
              </button>
            </div>

            {/* ADMIN-only shortcut to platform health. */}
            {isPlatformAdmin && (
              <button
                onClick={() => openModule('systemMonitoring')}
                className={`p-2 rounded-lg transition-all duration-300 backdrop-blur-md border ${
                  isDarkMode
                    ? 'bg-rose-500/15 text-rose-300 border-rose-500/30 shadow-md hover:bg-rose-500/25'
                    : 'bg-rose-50/70 text-rose-600 hover:bg-rose-100 border-rose-200/60 hover:border-rose-300'
                }`}
                style={{
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)'
                }}
                title={t('admin_shortcut_tooltip', { defaultValue: 'Open system monitoring' })}
              >
                <Shield size={16} className="transition-transform duration-300 hover:scale-110" />
              </button>
            )}

            {/* Window Layout Management */}
            <div className="relative">
              <button
                onClick={() => setShowLayoutMenu(!showLayoutMenu)}
                className={`p-2 rounded-lg transition-all duration-300 backdrop-blur-md border ${
                  isDarkMode
                    ? 'bg-gray-800/40 text-blue-400 border-gray-600/30 shadow-md'
                    : 'bg-white/20 text-gray-600 hover:bg-white/30 border-white/20 hover:border-white/40'
                }`}
                style={{
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)'
                }}
                title={t('window_layout_management')}
              >
                <Layout size={16} className="transition-transform duration-300 hover:rotate-12" />
              </button>

              {/* Layout Management Dropdown */}
              {showLayoutMenu && (
                <div 
                  className={`layout-dropdown absolute top-full right-0 mt-2 w-80 rounded-2xl shadow-2xl border p-0 z-50 overflow-hidden ${
                    isDarkMode
                      ? 'bg-gray-900/95 border-gray-700/50'
                      : 'bg-white/95 border-white/30'
                  }`}
                  style={{
                    backdropFilter: 'blur(24px) saturate(180%)',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255,255,255,0.05)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className={`px-5 py-4 border-b ${isDarkMode ? 'border-gray-700/50' : 'border-gray-200/60'}`}>
                    <div className={`text-sm font-bold tracking-tight ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                      {t('workspace_mode')}
                    </div>
                  </div>
                  <div className="p-3 space-y-2">
                    {([
                      { mode: 'fixed' as const, icon: <Layout size={16} />, label: t('workspace_fixed'), description: t('workspace_fixed_desc') },
                      { mode: 'mac' as const, icon: <Monitor size={16} />, label: t('workspace_mac'), description: t('workspace_mac_desc') }
                    ]).map(option => (
                      <button
                        key={option.mode}
                        onClick={() => { setWindowMode(option.mode); setShowLayoutMenu(false) }}
                        className={`w-full flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                          windowMode === option.mode
                            ? isDarkMode ? 'border-blue-500 bg-blue-500/10' : 'border-blue-400 bg-blue-50'
                            : isDarkMode ? 'border-gray-700 hover:bg-gray-800' : 'border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <span className={windowMode === option.mode ? 'text-blue-500' : 'text-gray-500'}>{option.icon}</span>
                        <span className="flex-1">
                          <span className={`block text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>{option.label}</span>
                          <span className={`block text-[11px] mt-0.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{option.description}</span>
                        </span>
                        {windowMode === option.mode && <CheckCircle size={16} className="text-blue-500" />}
                      </button>
                    ))}
                  </div>

                </div>
              )}
            </div>

            {/* Language switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangOpen(!langOpen)}
                className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-all duration-300 backdrop-blur-md border ${
                  isDarkMode
                    ? 'bg-gray-800/40 text-gray-300 border-gray-600/30 hover:bg-gray-700/40'
                    : 'bg-white/20 text-gray-600 hover:bg-white/30 border-white/20 hover:border-white/40'
                }`}
                style={{
                  backdropFilter: 'blur(12px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(12px) saturate(180%)'
                }}
                title={t('language')}
              >
                <Globe size={16} />
                <span className="text-xs font-semibold uppercase">{i18n.language?.substring(0, 2) || 'en'}</span>
              </button>
              {langOpen && (
                <div className={`absolute right-0 mt-2 w-36 rounded-lg shadow-xl border overflow-hidden z-50 ${
                  isDarkMode
                    ? 'bg-gray-800/95 border-gray-600/50'
                    : 'bg-white/95 border-gray-200'
                }`} style={{ backdropFilter: 'blur(20px)' }}>
                  <button
                    onClick={() => { i18n.changeLanguage('en'); setLangOpen(false) }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                      i18n.language?.startsWith('en')
                        ? 'bg-blue-500/20 text-blue-400 font-medium'
                        : isDarkMode ? 'text-gray-300 hover:bg-gray-700/50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>EN</span> <span>{t('english')}</span>
                  </button>
                  <button
                    onClick={() => { i18n.changeLanguage('fr'); setLangOpen(false) }}
                    className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2 transition-colors ${
                      i18n.language?.startsWith('fr')
                        ? 'bg-blue-500/20 text-blue-400 font-medium'
                        : isDarkMode ? 'text-gray-300 hover:bg-gray-700/50' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <span>FR</span> <span>{t('french')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Dark mode toggle */}
            <button
              onClick={() => applyDarkMode(!isDarkMode)}
              className={`p-2 rounded-lg transition-all duration-300 backdrop-blur-md border ${
                isDarkMode
                  ? 'bg-gray-800/40 text-yellow-400 border-gray-600/30 shadow-md'
                  : 'bg-white/20 text-gray-600 hover:bg-white/30 border-white/20 hover:border-white/40'
              }`}
              style={{
                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)'
              }}
              title={isDarkMode ? t('switch_light_mode') : t('switch_dark_mode')}
            >
              {isDarkMode ? (
                <Sun size={16} className="transition-transform duration-300 hover:rotate-12" />
              ) : (
                <Moon size={16} className="transition-transform duration-300 hover:-rotate-12" />
              )}
            </button>

            {/* Logout button */}
            <button
              onClick={logout}
              className={`p-2 rounded-lg transition-all duration-300 backdrop-blur-md border ${
                isDarkMode
                  ? 'bg-red-500/20 text-red-300 border-red-500/30 hover:bg-red-500/30'
                  : 'bg-red-50/50 text-red-600 hover:bg-red-100/50 border-red-200/50 hover:border-red-300/50'
              }`}
              style={{
                backdropFilter: 'blur(12px) saturate(180%)',
                WebkitBackdropFilter: 'blur(12px) saturate(180%)'
              }}
              title={t('logout')}
            >
              <LogOut size={16} className="transition-transform duration-200 hover:scale-110" />
            </button>
          </div>
        </div>
      </div>

      {/* Spacer for fixed menu bar */}
      <div className="flex-shrink-0 h-[60px]" />

      {windowMode === 'fixed' && (
        <>
          <FixedWorkspaceNav
            ariaLabel={t('workspace_navigation')}
            darkMode={isDarkMode}
            profiles={[
              { id: 'tracking', label: t('workspace_group_tracking'), icon: <Map size={15} /> },
              { id: 'operations', label: t('workspace_group_operations'), icon: <Briefcase size={15} /> },
              { id: 'administration', label: t('workspace_group_management'), icon: <Building2 size={15} /> }
            ]}
            activeProfile={fixedProfile}
            onProfileChange={(profileId) => {
              const profile = profileId as FixedProfile
              setFixedProfile(profile)
              if (profile === 'tracking') setActiveFixedPanel('map')
              else if (fixedProfile !== profile) {
                setActiveFixedPanel(profile === 'operations' ? 'vehicleOperations' : 'baliseManagement')
              }
              setFixedDrawer(null)
            }}
            modules={(fixedProfile === 'operations'
              ? fixedNavigationGroups[1].panels
              : fixedProfile === 'administration'
                ? fixedNavigationGroups[2].panels
                // Tracking shows the same shortcuts as the window-mode dock:
                // the customer found the fixed workspace had "no menu"
                // because the map profile offered nothing to click.
                : [...fixedNavigationGroups[1].panels, ...fixedNavigationGroups[2].panels]
            ).filter(isModuleAvailable).map(panelId => ({
              id: panelId,
              label: panelMeta[panelId].label,
              icon: panelMeta[panelId].icon,
              badge: panelId === 'fleetAlerts' ? activeAlertCount : undefined
            }))}
            activeModule={activeFixedPanel}
            onModuleChange={(panelId) => openModule(panelId as PanelId)}
            trailing={fixedProfile === 'tracking' ? (
              <button
                onClick={() => setMobileVehiclePanelOpen(true)}
                className={`flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-semibold md:hidden ${isDarkMode ? 'text-gray-300 hover:bg-gray-800' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Car size={14} />{t('workspace_show_vehicles')} ({state.vehicles.length})
              </button>
            ) : undefined}
          />
          <main className={`relative flex-1 min-h-0 overflow-hidden ${isDarkMode ? 'bg-gray-800' : 'bg-slate-100'}`}>
            {fixedProfile === 'tracking' ? (
              <div className="absolute inset-0 flex min-h-0 min-w-0">
                <CollapsibleSidePanel
                  collapsed={trackingPanelCollapsed}
                  onToggle={() => setTrackingPanelCollapsed(current => !current)}
                  darkMode={isDarkMode}
                  icon={<Car size={16} />}
                  collapseLabel={t('workspace_collapse_panel')}
                  expandLabel={t('workspace_expand_panel')}
                >
                  {renderPanelContent('vehicles')}
                </CollapsibleSidePanel>
                {/* The map has its own Speed/Stops/Timeline/Stats tabs — no
                    separate analytics dock, it duplicated the speed graph. */}
                <section className="relative flex min-w-0 min-h-0 flex-1 flex-col">
                  <div className="min-h-0 min-w-0 flex-1">
                    {renderPanelContent('map')}
                  </div>
                </section>
                {mobileVehiclePanelOpen && (
                  <div className="absolute inset-0 z-30 flex md:hidden">
                    <div className={`flex h-full w-[85vw] max-w-[400px] min-w-0 flex-col shadow-2xl ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
                      <div className={`flex h-10 shrink-0 items-center justify-between border-b px-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <span className={`flex items-center gap-2 text-sm font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'}`}>
                          <Car size={15} />{t('fleet_vehicles')}
                        </span>
                        <button onClick={() => setMobileVehiclePanelOpen(false)} className="rounded p-1.5 hover:bg-black/10" title={t('close')} aria-label={t('close')}>
                          <X size={16} />
                        </button>
                      </div>
                      <div className="min-h-0 flex-1 overflow-hidden">{renderPanelContent('vehicles')}</div>
                    </div>
                    <div className="flex-1 bg-black/40" onClick={() => setMobileVehiclePanelOpen(false)} />
                  </div>
                )}
                {fixedDrawer && (
                  <ContextDrawer
                    overlay
                    title={panelMeta[fixedDrawer]?.label}
                    icon={panelMeta[fixedDrawer]?.icon}
                    onClose={() => setFixedDrawer(null)}
                    darkMode={isDarkMode}
                    storageKey="fleet.workspace.drawerWidth"
                    closeLabel={t('close')}
                    resizeLabel={t('workspace_drawer_resize')}
                  >
                    {renderPanelContent(fixedDrawer)}
                  </ContextDrawer>
                )}
              </div>
            ) : (
              <div className="absolute inset-0 flex min-h-0 min-w-0">
                {/* Modules are h-full with internal scrolling; overflow-hidden keeps
                    them integrated edge-to-edge like the Tracking cockpit instead of
                    scrolling the whole pane. */}
                <section className="min-w-0 min-h-0 flex-1 overflow-hidden">
                  {renderPanelContent(activeFixedPanel)}
                </section>
                {fixedDrawer && (
                  <ContextDrawer
                    title={panelMeta[fixedDrawer]?.label}
                    icon={panelMeta[fixedDrawer]?.icon}
                    onClose={() => setFixedDrawer(null)}
                    darkMode={isDarkMode}
                    storageKey="fleet.workspace.drawerWidth"
                    closeLabel={t('close')}
                    resizeLabel={t('workspace_drawer_resize')}
                  >
                    {renderPanelContent(fixedDrawer)}
                  </ContextDrawer>
                )}
              </div>
            )}
          </main>
        </>
      )}

      {/* ===== MAC MODE: Desktop + windows + taskbar ===== */}
      {windowMode === 'mac' && (
      <>
      <div className={`flex-1 relative transition-colors duration-500 ${isDarkMode ? 'bg-gray-900' : 'bg-[#E9ECF1]'}`}>

        {/* Mobile menu button */}
        <div className="md:hidden absolute top-4 left-4 z-50">
          <button
            onClick={() => updateWindow('vehicles', { visible: true, minimized: false })}
            className="bg-white rounded-lg shadow-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            ?? Vehicles ({state.vehicles.length})
          </button>
        </div>

      </div>

      {/* Floating windows */}

      <ResponsiveMacWindow
        id="map"
        title={t('fleet_map')}
        icon={<Map size={14} className="text-gray-600" />}
        isVisible={windows.map.visible}
        isMinimized={windows.map.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('map', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('map', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('map', { minimized: false })
        }}
        onFocus={() => bringToFront('map')}
        zIndex={windowZIndex.map}
        initialPosition={{ x: 0, y: 0 }}
        darkMode={isDarkMode}

        minWidth={400}
        minHeight={300}
        maxWidth={2600}
        maxHeight={1600}
      >
        <ResponsiveFleetMap
          vehicles={mapVehicles}
          selectedVehicle={state.selectedVehicle}
          onVehicleSelect={handleVehicleSelect}
          timeRange={timeRange}
          timelineHover={timelineHover}
          sharedDateRange={sharedDateRange}
          onDateRangeChange={setSharedDateRange}
          onCreateFromPosition={handleCreateFromVehiclePosition}
          onCreateAlert={handleCreateAlertForVehicle}
          onOpenAlertDetail={openAlertFromMap}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="vehicles"
        title={t('fleet_vehicles')}
        icon={<Activity size={14} className="text-gray-600" />}
        isVisible={windows.vehicles.visible}
        isMinimized={windows.vehicles.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('vehicles', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('vehicles', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('vehicles', { minimized: false })
        }}
        onFocus={() => bringToFront('vehicles')}
        zIndex={windowZIndex.vehicles}
        initialPosition={{ x: 0, y: 0 }}
        darkMode={isDarkMode}

        minWidth={250}
        minHeight={280}
        maxWidth={1400}
        maxHeight={1000}
      >
        <FleetVehicleExplorer
          vehicles={state.vehicles}
          selectedVehicle={state.selectedVehicle}
          onVehicleSelect={handleVehicleSelect}
          onFilteredChange={handleVehicleListFiltered}
          onShowOnMap={focusVehicleOnMap}
          dateRange={sharedDateRange}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="speed"
        title={state.selectedVehicle ? `${t('speed_analysis')} - ${state.selectedVehicle.name}` : t('speed_analysis')}
        icon={<BarChart3 size={14} className="text-gray-600" />}
        isVisible={windows.speed.visible}
        isMinimized={windows.speed.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('speed', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('speed', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('speed', { minimized: false })
        }}
        onFocus={() => bringToFront('speed')}
        zIndex={windowZIndex.speed}
        initialPosition={{ x: 0, y: 0 }}
        darkMode={isDarkMode}

        minWidth={280}
        minHeight={200}
        maxWidth={800}
        maxHeight={600}
      >
          <SpeedChart 
            vehicle={state.selectedVehicle} 
            timeRange={timeRange}
            onTimelineHover={handleTimelineHover}
            dateRange={sharedDateRange}
          />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="events"
        title={state.selectedVehicle ? `${t('event_analysis')} - ${state.selectedVehicle.name}` : t('event_analysis')}
        icon={<Activity size={14} className="text-gray-600" />}
        isVisible={windows.events.visible}
        isMinimized={windows.events.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('events', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('events', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('events', { minimized: false })
        }}
        onFocus={() => bringToFront('events')}
        zIndex={windowZIndex.events}
        initialPosition={{ x: 0, y: 0 }}
        darkMode={isDarkMode}

        minWidth={300}
        minHeight={250}
        maxWidth={900}
        maxHeight={700}
      >
          <EventChart 
            vehicle={state.selectedVehicle} 
            timeRange={timeRange}
            onTimelineHover={handleTimelineHover}
            dateRange={sharedDateRange}
          />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="corridors"
        title={t('corridor_management')}
        icon={<Route size={14} className="text-gray-600" />}
        isVisible={windows.corridors.visible}
        isMinimized={windows.corridors.minimized}
        onClose={() => updateWindow('corridors', { visible: false })}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('corridors', { visible: true, minimized: true })
        }}
        onRestore={() => updateWindow('corridors', { minimized: false })}
        onFocus={() => bringToFront('corridors')}
        zIndex={windowZIndex.corridors}
        initialPosition={{ x: 100, y: 80 }}
        darkMode={isDarkMode}

        minWidth={1200}
        minHeight={800}
        maxWidth={1800}
        maxHeight={1400}
      >
        <CorridorManager 
          onCorridorSelect={(corridor) => {
            console.log('Selected corridor:', corridor)
          }}
          onCorridorsChange={(corridors) => {
            console.log('Corridors updated:', corridors)
          }}
          showMessage={showMacMessage}
          showConfirm={showMacConfirm}
          creationRequest={corridorCreationRequest}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="users"
        title={t('user_management_title')}
        icon={<Users size={14} className="text-gray-600" />}
        isVisible={windows.users.visible}
        isMinimized={windows.users.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('users', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('users', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('users', { minimized: false })
        }}
        onFocus={() => bringToFront('users')}
        zIndex={windowZIndex.users || 45}
        initialPosition={{ x: 200, y: 120 }}
        darkMode={isDarkMode}

        minWidth={600}
        minHeight={500}
        maxWidth={1000}
        maxHeight={800}
      >
        <TabbedUserRoleManagement 
          onUserSelect={(user) => {
            console.log('Selected user:', user)
          }}
          onUsersChange={(users) => {
            console.log('Users updated:', users)
          }}
          showMessage={showMacMessage}
          showConfirm={showMacConfirm}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="fleet-staff"
        title={t('fleet_staff_management')}
        icon={<Users size={14} className="text-gray-600" />}
        isVisible={windows.fleetStaff.visible}
        isMinimized={windows.fleetStaff.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('fleetStaff', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('fleetStaff', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('fleetStaff', { minimized: false })
        }}
        onFocus={() => bringToFront('fleetStaff')}
        zIndex={windowZIndex.fleetStaff || 47}
        initialPosition={{ x: 100, y: 100 }}
        darkMode={isDarkMode}

        minWidth={1000}
        minHeight={700}
        maxWidth={1400}
        maxHeight={1000}
      >
        <FleetStaffManagement 
          showMessage={showMacMessage}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="vehicle-operations"
        title={t('fleet_vehicle_operations')}
        icon={<Truck size={14} className="text-gray-600" />}
        isVisible={windows.vehicleOperations.visible}
        isMinimized={windows.vehicleOperations.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('vehicleOperations', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('vehicleOperations', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('vehicleOperations', { minimized: false })
        }}
        onFocus={() => bringToFront('vehicleOperations')}
        zIndex={windowZIndex.vehicleOperations || 48}
        initialPosition={{ x: 150, y: 150 }}
        darkMode={isDarkMode}

        minWidth={1200}
        minHeight={800}
        maxWidth={1600}
        maxHeight={1200}
      >
        <FleetVehicleOperations 
          showMessage={showMacMessage}
          onShowOnMap={handleShowVehicleOnMap}
          onCreateVehicleZone={handleCreateVehicleZone}
          onManageVehicleZone={handleManageVehicleZone}
          onManageVehicleMission={handleManageVehicleMission}
          onCreateVehicleAlertRule={(vehicle) => openVehicleAlertRule(vehicle)}
          onEditVehicleAlertRule={(rule, vehicle) => openVehicleAlertRule(vehicle, rule)}
          onOpenVehicleControlCenter={openVehicleControlCenter}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="vehicle-control"
        title={vehicleControlBinding
          ? `${t('vcc_title', { defaultValue: 'Vehicle Control Center' })} — ${vehicleControlBinding.vehicle.custom_name || vehicleControlBinding.vehicle.vehicle_code}`
          : t('vcc_title', { defaultValue: 'Vehicle Control Center' })}
        icon={<Gauge size={14} className="text-gray-600" />}
        isVisible={windows.vehicleControl.visible}
        isMinimized={windows.vehicleControl.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation() }
          updateWindow('vehicleControl', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation() }
          updateWindow('vehicleControl', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation() }
          updateWindow('vehicleControl', { minimized: false })
        }}
        onFocus={() => bringToFront('vehicleControl')}
        zIndex={windowZIndex.vehicleControl || 50}
        initialPosition={{ x: 180, y: 90 }}
        darkMode={isDarkMode}
        minWidth={900}
        minHeight={650}
        maxWidth={1500}
        maxHeight={1000}
      >
        {vehicleControlBinding && (
          <VehicleDetailsModal
            embedded
            isOpen
            vehicle={vehicleControlBinding.vehicle}
            activeTab={vehicleControlBinding.tab}
            onTabChange={(tab) => setVehicleControlBinding(previous => previous ? { ...previous, tab, startEditing: false } : previous)}
            startEditing={vehicleControlBinding.startEditing}
            onClose={() => updateWindow('vehicleControl', { visible: false })}
            onSuccess={() => showMacMessage(t('vehicle_updated', { defaultValue: 'Vehicle updated successfully' }), 'success')}
            onShowOnMap={handleShowVehicleOnMap}
            onCreateZone={handleCreateVehicleZone}
            onManageZone={handleManageVehicleZone}
            onManageMission={handleManageVehicleMission}
            onCreateAlertRule={(vehicle) => openVehicleAlertRule(vehicle)}
            onEditAlertRule={(rule, vehicle) => openVehicleAlertRule(vehicle, rule)}
            showMessage={showMacMessage}
          />
        )}
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="mission-control"
        title={t('mission_control_system')}
        icon={<Truck size={14} className="text-gray-600" />}
        isVisible={windows.missionControl.visible}
        isMinimized={windows.missionControl.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('missionControl', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('missionControl', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('missionControl', { minimized: false })
        }}
        onFocus={() => bringToFront('missionControl')}
        zIndex={windowZIndex.missionControl || 46}
        initialPosition={{ x: 300, y: 150 }}
        darkMode={isDarkMode}

        minWidth={800}
        minHeight={600}
        maxWidth={1400}
        maxHeight={900}
      >
        <MissionDashboard 
          showMessage={showMacMessage}
        />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="balise-management"
        title={t('balise_management')}
        icon={<Radio size={14} className="text-gray-600" />}
        isVisible={windows.baliseManagement.visible}
        isMinimized={windows.baliseManagement.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('baliseManagement', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('baliseManagement', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('baliseManagement', { minimized: false })
        }}
        onFocus={() => bringToFront('baliseManagement')}
        zIndex={windowZIndex.baliseManagement || 49}
        initialPosition={{ x: 80, y: 60 }}
        darkMode={isDarkMode}

        minWidth={900}
        minHeight={600}
        maxWidth={1400}
        maxHeight={900}
      >
        <BaliseManagement showMessage={showMacMessage} />
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="customers"
        title={t('customer_management')}
        icon={<Building2 size={14} className="text-gray-600" />}
        isVisible={windows.customers.visible}
        isMinimized={windows.customers.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('customers', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('customers', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('customers', { minimized: false })
        }}
        onFocus={() => bringToFront('customers')}
        zIndex={windowZIndex.customers || 50}
        initialPosition={{ x: 120, y: 80 }}
        darkMode={isDarkMode}

        minWidth={800}
        minHeight={600}
        maxWidth={1300}
        maxHeight={900}
      >
        <CustomerManagement showMessage={showMacMessage} />
      </ResponsiveMacWindow>

      {/* System Monitoring — admin-only platform health window. */}
      <ResponsiveMacWindow
        id="system-monitoring"
        title={t('system_monitoring_title', { defaultValue: 'System monitoring' })}
        icon={<Activity size={14} className="text-rose-600" />}
        isVisible={windows.systemMonitoring.visible}
        isMinimized={windows.systemMonitoring.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('systemMonitoring', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('systemMonitoring', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('systemMonitoring', { minimized: false })
        }}
        onFocus={() => bringToFront('systemMonitoring')}
        zIndex={windowZIndex.systemMonitoring || 51}
        initialPosition={{ x: 160, y: 100 }}
        darkMode={isDarkMode}

        minWidth={720}
        minHeight={520}
        maxWidth={1200}
        maxHeight={900}
      >
        <SystemMonitoring showMessage={showMacMessage} />
      </ResponsiveMacWindow>

      {/* Settings Center — profile/preferences and validated application
          parameters. This is intentionally independent from Monitoring. */}
      <ResponsiveMacWindow
        id="settings-center"
        title={t('settings_center', { defaultValue: 'Settings Center' })}
        icon={<Settings size={14} className="text-blue-600" />}
        isVisible={windows.settings.visible}
        isMinimized={windows.settings.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('settings', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('settings', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('settings', { minimized: false })
        }}
        onFocus={() => bringToFront('settings')}
        zIndex={windowZIndex.settings || 53}
        initialPosition={{ x: 190, y: 70 }}
        darkMode={isDarkMode}
        minWidth={820}
        minHeight={580}
        maxWidth={1400}
        maxHeight={920}
      >
        <SettingsCenter showMessage={showMacMessage} />
      </ResponsiveMacWindow>

      {/* Fleet Alerts ? live geofence/corridor alerts (fleet_alerts) with
          acknowledge/resolve lifecycle and diffusion rule management. */}
      <ResponsiveMacWindow
        id="fleet-alerts"
        title={t('fleet_alerts_title', { defaultValue: 'Fleet Alerts' })}
        icon={<AlertTriangle size={14} className="text-red-600" />}
        isVisible={windows.fleetAlerts.visible}
        isMinimized={windows.fleetAlerts.minimized}
        onClose={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('fleetAlerts', { visible: false })
        }}
        onMinimize={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('fleetAlerts', { visible: true, minimized: true })
        }}
        onRestore={(e) => {
          if (e) { e.preventDefault(); e.stopPropagation(); }
          updateWindow('fleetAlerts', { minimized: false })
        }}
        onFocus={() => bringToFront('fleetAlerts')}
        zIndex={windowZIndex.fleetAlerts || 57}
        initialPosition={{ x: 200, y: 110 }}
        darkMode={isDarkMode}

        minWidth={560}
        minHeight={480}
        maxWidth={1000}
        maxHeight={900}
      >
        <FleetAlertsPanel showMessage={showMacMessage} showConfirm={showMacConfirm} creationRequest={alertCreationRequest} focusAlert={alertFocus} />
      </ResponsiveMacWindow>

      {/* Reports ? on-demand report runs with CSV/Excel/PDF export and the
          scheduled-email manager. */}
      <ResponsiveMacWindow
        id="reports"
        title={t('reports.windowTitle', { defaultValue: 'Reports' })}
        icon={<BarChart3 size={14} className="text-violet-600" />}
        isVisible={windows.reports.visible}
        isMinimized={windows.reports.minimized}
        onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('reports', { visible: false }) }}
        onMinimize={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('reports', { visible: true, minimized: true }) }}
        onRestore={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('reports', { minimized: false }) }}
        onFocus={() => bringToFront('reports')}
        zIndex={windowZIndex.reports || 61}
        initialPosition={{ x: 150, y: 90 }}
        darkMode={isDarkMode}
        minWidth={820}
        minHeight={560}
        maxWidth={1500}
        maxHeight={1000}
      >
        <ReportsPanel showMessage={showMacMessage} showConfirm={showMacConfirm} />
      </ResponsiveMacWindow>

      {/* P8 ? Asset-scoped Smart Lock window. Bound to the (assetId, imei)
          pair the picker (or the 1-peripheral auto-open path) hands us. */}
      <ResponsiveMacWindow
        id="asset-smart-lock"
        title={
          assetWindowBindings.assetSmartLock
            ? t('asset_lock_title_bound', { defaultValue: 'Smart Lock ? {{name}}', name: assetWindowBindings.assetSmartLock.deviceName || assetWindowBindings.assetSmartLock.imei })
            : t('asset_lock_default_name', { defaultValue: 'Smart Lock' })
        }
        icon={<Lock size={14} className="text-amber-600" />}
        isVisible={windows.assetSmartLock.visible}
        isMinimized={windows.assetSmartLock.minimized}
        onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetSmartLock', { visible: false }) }}
        onMinimize={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetSmartLock', { visible: true, minimized: true }) }}
        onRestore={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetSmartLock', { minimized: false }) }}
        onFocus={() => bringToFront('assetSmartLock')}
        zIndex={windowZIndex.assetSmartLock || 53}
        initialPosition={{ x: 200, y: 130 }}
        darkMode={isDarkMode}

        minWidth={720}
        minHeight={520}
      >
        {assetWindowBindings.assetSmartLock ? (
          <AssetSmartLockWindow
            assetId={assetWindowBindings.assetSmartLock.assetId}
            imei={assetWindowBindings.assetSmartLock.imei}
            deviceName={assetWindowBindings.assetSmartLock.deviceName}
            showMessage={showMacMessage}
          />
        ) : (
          <div className="p-4 text-sm text-gray-500 italic">
            {t('asset_window_no_binding', { defaultValue: 'Select an asset with a smart-lock peripheral to populate this window.' })}
          </div>
        )}
      </ResponsiveMacWindow>

      {/* P8 ? Asset-scoped AI Dashcam window. */}
      <ResponsiveMacWindow
        id="asset-dashcam"
        title={
          assetWindowBindings.assetDashcam
            ? t('asset_dashcam_title_bound', { defaultValue: 'Dashcam ? {{name}}', name: assetWindowBindings.assetDashcam.deviceName || assetWindowBindings.assetDashcam.imei })
            : t('asset_dashcam_default_name', { defaultValue: 'AI Dashcam' })
        }
        icon={<Cpu size={14} className="text-purple-600" />}
        isVisible={windows.assetDashcam.visible}
        isMinimized={windows.assetDashcam.minimized}
        onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetDashcam', { visible: false }) }}
        onMinimize={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetDashcam', { visible: true, minimized: true }) }}
        onRestore={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetDashcam', { minimized: false }) }}
        onFocus={() => bringToFront('assetDashcam')}
        zIndex={windowZIndex.assetDashcam || 54}
        initialPosition={{ x: 220, y: 150 }}
        darkMode={isDarkMode}

        minWidth={760}
        minHeight={560}
      >
        {assetWindowBindings.assetDashcam ? (
          <AssetDashcamWindow
            assetId={assetWindowBindings.assetDashcam.assetId}
            imei={assetWindowBindings.assetDashcam.imei}
            deviceName={assetWindowBindings.assetDashcam.deviceName}
            showMessage={showMacMessage}
          />
        ) : (
          <div className="p-4 text-sm text-gray-500 italic">
            {t('asset_window_no_binding', { defaultValue: 'Select an asset with a dashcam peripheral to populate this window.' })}
          </div>
        )}
      </ResponsiveMacWindow>

      {/* P8 ? Asset-scoped OBD telemetry window. */}
      <ResponsiveMacWindow
        id="asset-obd"
        title={
          assetWindowBindings.assetObd
            ? t('asset_obd_title_bound', { defaultValue: 'OBD ? {{name}}', name: assetWindowBindings.assetObd.deviceName || assetWindowBindings.assetObd.imei })
            : t('asset_obd_default_name', { defaultValue: 'OBD Telemetry' })
        }
        icon={<Cpu size={14} className="text-cyan-600" />}
        isVisible={windows.assetObd.visible}
        isMinimized={windows.assetObd.minimized}
        onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetObd', { visible: false }) }}
        onMinimize={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetObd', { visible: true, minimized: true }) }}
        onRestore={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetObd', { minimized: false }) }}
        onFocus={() => bringToFront('assetObd')}
        zIndex={windowZIndex.assetObd || 55}
        initialPosition={{ x: 240, y: 170 }}
        darkMode={isDarkMode}

        minWidth={820}
        minHeight={560}
      >
        {assetWindowBindings.assetObd ? (
          <AssetObdWindow
            assetId={assetWindowBindings.assetObd.assetId}
            imei={assetWindowBindings.assetObd.imei}
            deviceName={assetWindowBindings.assetObd.deviceName}
            showMessage={showMacMessage}
          />
        ) : (
          <div className="p-4 text-sm text-gray-500 italic">
            {t('asset_window_no_binding', { defaultValue: 'Select an asset with an OBD peripheral to populate this window.' })}
          </div>
        )}
      </ResponsiveMacWindow>

      {/* P8 ? Asset-scoped paired-tags window. */}
      <ResponsiveMacWindow
        id="asset-tags"
        title={
          assetWindowBindings.assetTags
            ? t('asset_tags_title_bound', { defaultValue: 'Tags ? {{name}}', name: assetWindowBindings.assetTags.deviceName || assetWindowBindings.assetTags.imei })
            : t('asset_tags_default_name', { defaultValue: 'Paired Tags' })
        }
        icon={<Cpu size={14} className="text-pink-600" />}
        isVisible={windows.assetTags.visible}
        isMinimized={windows.assetTags.minimized}
        onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetTags', { visible: false }) }}
        onMinimize={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetTags', { visible: true, minimized: true }) }}
        onRestore={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetTags', { minimized: false }) }}
        onFocus={() => bringToFront('assetTags')}
        zIndex={windowZIndex.assetTags || 56}
        initialPosition={{ x: 260, y: 190 }}
        darkMode={isDarkMode}

        minWidth={680}
        minHeight={520}
      >
        {assetWindowBindings.assetTags ? (
          <AssetTagsWindow
            assetId={assetWindowBindings.assetTags.assetId}
            imei={assetWindowBindings.assetTags.imei}
            deviceName={assetWindowBindings.assetTags.deviceName}
            showMessage={showMacMessage}
          />
        ) : (
          <div className="p-4 text-sm text-gray-500 italic">
            {t('asset_window_no_binding', { defaultValue: 'Select an asset with paired tags to populate this window.' })}
          </div>
        )}
      </ResponsiveMacWindow>

      <ResponsiveMacWindow
        id="asset-cockpit"
        title={
          cockpitBinding
            ? t('cockpit_title_bound', { defaultValue: 'Cockpit — {{name}}', name: cockpitBinding.label || cockpitBinding.assetId })
            : t('cockpit_title', { defaultValue: 'Vehicle cockpit' })
        }
        icon={<Gauge size={14} className="text-teal-700" />}
        isVisible={windows.assetCockpit.visible}
        isMinimized={windows.assetCockpit.minimized}
        onClose={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetCockpit', { visible: false }) }}
        onMinimize={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetCockpit', { visible: true, minimized: true }) }}
        onRestore={(e) => { if (e) { e.preventDefault(); e.stopPropagation(); } updateWindow('assetCockpit', { minimized: false }) }}
        onFocus={() => bringToFront('assetCockpit')}
        zIndex={windowZIndex.assetCockpit || 53}
        initialPosition={{ x: 160, y: 90 }}
        darkMode={isDarkMode}
        minWidth={720}
        minHeight={520}
        maxWidth={1200}
        maxHeight={900}
      >
        {cockpitBinding ? (
          <AssetCockpitWindow
            assetId={cockpitBinding.assetId}
            vehicleLabel={cockpitBinding.label}
            showMessage={showMacMessage}
            onOpenMap={() => {
              updateWindow('map', { visible: true, minimized: false })
              bringToFront('map')
            }}
            onOpenCapability={(windowId, imei, deviceName) => {
              if (['assetSmartLock', 'assetDashcam', 'assetObd', 'assetTags'].includes(windowId)) {
                setAssetWindowBindings((prev) => ({
                  ...prev,
                  [windowId]: { assetId: cockpitBinding.assetId, imei, deviceName },
                }))
                updateWindow(windowId as any, { visible: true, minimized: false })
                bringToFront(windowId)
              }
            }}
          />
        ) : (
          <div className="p-4 text-sm text-gray-500 italic">
            {t('cockpit_no_binding', { defaultValue: 'Select a vehicle and open its cockpit to see balises and captors.' })}
          </div>
        )}
      </ResponsiveMacWindow>

      {/* Mac-Style Auto-Hide Taskbar (Bottom) */}
      <div 
        className={`fixed left-1/2 transform -translate-x-1/2 z-[260] transition-all duration-500 ease-out ${
          taskbarVisible ? 'bottom-0' : '-bottom-full'
        }`}
        onMouseEnter={() => {
          setTaskbarHovered(true)
          setTaskbarVisible(true)
        }}
        onMouseLeave={() => {
          setTaskbarHovered(false)
        }}
      >
        <div 
          className="bg-black/20 backdrop-blur-xl rounded-t-2xl border border-white/10 shadow-2xl transition-all duration-300 ease-out px-3 py-2 mb-0"
          style={{
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)'
          }}
        >
          <div className="flex items-center space-x-2">

            {/* Vehicles */}
            <button
              onClick={() => {
                console.log('?? Taskbar Vehicles button clicked')
                if (windows.vehicles.visible && !windows.vehicles.minimized) {
                  bringToFront('vehicles')
                } else {
                  updateWindow('vehicles', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('vehicles')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.vehicles.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{ 
                  backgroundColor: '#3B82F680',
                  boxShadow: '0 8px 32px #3B82F640'
                }}
              >
                <Car size={11} className="text-white" />
              </div>
              {windows.vehicles.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* Vehicle count badge */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                {state.vehicles.length}
              </div>
            </button>

            {/* Vehicle cockpit — all balises / captors for selected vehicle */}
            <button
              onClick={() => {
                if (!state.selectedVehicle) {
                  showMacMessage(t('cockpit_select_vehicle', { defaultValue: 'Select a vehicle first' }), 'warning')
                  return
                }
                openAssetCockpit(state.selectedVehicle)
              }}
              disabled={!state.selectedVehicle}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9 ${!state.selectedVehicle ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={t('cockpit_title', { defaultValue: 'Vehicle cockpit' })}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.assetCockpit.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{
                  backgroundColor: '#0F766E80',
                  boxShadow: '0 8px 32px #0F766E40'
                }}
              >
                <Gauge size={12} className="text-white" />
              </div>
              {windows.assetCockpit.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
            </button>

            {/* Map */}
            <button
              onClick={() => {
                console.log('??? Taskbar Map button clicked')
                console.log('?? Current map window state:', windows.map)
                if (windows.map.visible && !windows.map.minimized) {
                  bringToFront('map')
                } else {
                  updateWindow('map', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('fleet_map')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.map.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{ 
                  backgroundColor: '#10B98180',
                  boxShadow: '0 8px 32px #10B98140'
                }}
              >
                <Map size={12} className="text-white" />
              </div>
              {windows.map.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
            </button>

            {/* Speed */}
            <button
              onClick={() => {
                console.log('? Taskbar Speed button clicked')
                if (windows.speed.visible && !windows.speed.minimized) {
                  bringToFront('speed')
                } else {
                  updateWindow('speed', { visible: true, minimized: false })
                }
              }}
              disabled={!state.selectedVehicle}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9 ${!state.selectedVehicle ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={t('speed_analysis')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.speed.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{ 
                  backgroundColor: '#F59E0B80',
                  boxShadow: '0 8px 32px #F59E0B40'
                }}
              >
                <Zap size={12} className="text-white" />
              </div>
              {windows.speed.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* Speed alerts badge */}
              {state.vehicles.filter(v => v.speed > 80).length > 0 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                  {state.vehicles.filter(v => v.speed > 80).length}
                </div>
              )}
            </button>

            {/* Events */}
            <button
              onClick={() => {
                console.log('?? Taskbar Events button clicked')
                if (windows.events.visible && !windows.events.minimized) {
                  bringToFront('events')
                } else {
                  updateWindow('events', { visible: true, minimized: false })
                }
              }}
              disabled={!state.selectedVehicle}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9 ${!state.selectedVehicle ? 'opacity-50 cursor-not-allowed' : ''}
              `}
              title={t('events_analysis')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.events.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{ 
                  backgroundColor: '#EF444480',
                  boxShadow: '0 8px 32px #EF444440'
                }}
              >
                <Activity size={12} className="text-white" />
              </div>
              {windows.events.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
            </button>

            {/* Separator */}
            <div className="w-px h-6 bg-white/20" />

            {/* Corridors & Geofences (Combined) */}
            <button
              onClick={() => {
                console.log('??? Taskbar Corridors button clicked')
                console.log('?? Current corridors window state:', windows.corridors)
                if (windows.corridors.visible && !windows.corridors.minimized) {
                  bringToFront('corridors')
                } else {
                  updateWindow('corridors', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('corridors_geofences')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.corridors.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{ 
                  backgroundColor: '#8B5CF680',
                  boxShadow: '0 8px 32px #8B5CF640'
                }}
              >
                <Route size={12} className="text-white" />
              </div>
              {windows.corridors.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* Combined count badge (Routes + Geofences) */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-purple-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                11
              </div>
            </button>

            {/* User Management */}
            <button
              onClick={() => {
                console.log('?? Taskbar Users button clicked')
                console.log('?? Current users window state:', windows.users)
                if (windows.users.visible && !windows.users.minimized) {
                  bringToFront('users')
                } else {
                  updateWindow('users', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('user_role_management')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.users.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{ 
                  backgroundColor: '#6366F180',
                  boxShadow: '0 8px 32px #6366F140'
                }}
              >
                <Users size={12} className="text-white" />
              </div>
              {windows.users.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* User count badge */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                3
              </div>
            </button>

            {/* Fleet Alerts ? live geofence/corridor alert center */}
            <button
              onClick={() => {
                if (windows.fleetAlerts.visible && !windows.fleetAlerts.minimized) {
                  bringToFront('fleetAlerts')
                } else {
                  updateWindow('fleetAlerts', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('fleet_alerts_title', { defaultValue: 'Fleet Alerts' })}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.fleetAlerts.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                  ${activeAlertCount > 0 && !windows.fleetAlerts.visible ? 'animate-pulse' : ''}
                `}
                style={{
                  background: windows.fleetAlerts.visible || activeAlertCount > 0
                    ? 'linear-gradient(135deg, rgba(220, 38, 38, 0.9) 0%, rgba(249, 115, 22, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  boxShadow: windows.fleetAlerts.visible || activeAlertCount > 0
                    ? '0 8px 32px rgba(220, 38, 38, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <Bell size={12} className="text-white" />
              </div>
              {windows.fleetAlerts.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* Live active-alert count */}
              {activeAlertCount > 0 && (
                <div className="absolute -top-1 -right-1 min-w-4 h-4 px-0.5 bg-red-600 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                  {activeAlertCount > 99 ? '99+' : activeAlertCount}
                </div>
              )}
            </button>

            {/* Reports ? on-demand runs, exports and scheduled emails */}
            <button
              onClick={() => {
                if (windows.reports.visible && !windows.reports.minimized) {
                  bringToFront('reports')
                } else {
                  updateWindow('reports', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('reports.windowTitle', { defaultValue: 'Reports' })}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.reports.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{
                  background: windows.reports.visible
                    ? 'linear-gradient(135deg, rgba(124, 58, 237, 0.9) 0%, rgba(59, 130, 246, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  boxShadow: windows.reports.visible
                    ? '0 8px 32px rgba(124, 58, 237, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <BarChart3 size={12} className="text-white" />
              </div>
              {windows.reports.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
            </button>

            {/* Fleet Staff Management */}
            <button
              onClick={() => {
                console.log('?? Taskbar Fleet Staff button clicked')
                if (windows.fleetStaff.visible && !windows.fleetStaff.minimized) {
                  bringToFront('fleetStaff')
                } else {
                  updateWindow('fleetStaff', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('fleet_staff_management')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.fleetStaff.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{
                  background: windows.fleetStaff.visible 
                    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.9) 0%, rgba(168, 85, 247, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  boxShadow: windows.fleetStaff.visible 
                    ? '0 8px 32px rgba(99, 102, 241, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <Shield size={12} className="text-white" />
              </div>
              {windows.fleetStaff.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* Badge showing total staff */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                9
              </div>
            </button>

            {/* Fleet Vehicle Operations */}
            <button
              onClick={() => {
                console.log('?? Taskbar Vehicle Operations button clicked')
                if (windows.vehicleOperations.visible && !windows.vehicleOperations.minimized) {
                  bringToFront('vehicleOperations')
                } else {
                  updateWindow('vehicleOperations', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('fleet_vehicle_operations')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.vehicleOperations.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{
                  background: windows.vehicleOperations.visible
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(16, 185, 129, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  boxShadow: windows.vehicleOperations.visible
                    ? '0 8px 32px rgba(34, 197, 94, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <Car size={12} className="text-white" />
              </div>
              {windows.vehicleOperations.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
              {/* Badge showing total vehicles */}
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold shadow-lg">
                10
              </div>
            </button>

            {/* Mission Control System */}
            <button
              onClick={() => {
                console.log('?? Taskbar Mission Control button clicked')
                if (windows.missionControl.visible && !windows.missionControl.minimized) {
                  bringToFront('missionControl')
                } else {
                  updateWindow('missionControl', { visible: true, minimized: false })
                }
              }}
              className={`
                relative flex flex-col items-center cursor-pointer group
                transition-all duration-300 ease-out transform hover:scale-110
                w-9 h-9
              `}
              title={t('mission_control_system')}
            >
              <div
                className={`
                  flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10
                  w-7 h-7 transition-all duration-300
                  ${windows.missionControl.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}
                `}
                style={{
                  background: windows.missionControl.visible 
                    ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.9) 0%, rgba(59, 130, 246, 0.8) 100%)'
                    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.05) 100%)',
                  backdropFilter: 'blur(20px) saturate(180%)',
                  WebkitBackdropFilter: 'blur(20px) saturate(180%)',
                  boxShadow: windows.missionControl.visible 
                    ? '0 8px 32px rgba(34, 197, 94, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.2)'
                    : '0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.1)'
                }}
              >
                <Target size={12} className="text-white" />
              </div>
              {windows.missionControl.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
            </button>

            {/* Divider - BM panels */}
            <div className="w-px h-6 bg-white/15 mx-1" />

            {/* Balise Management ? unified inventory across all brands
                (Jointech, MaxVision, JIMI). Brand is a filter chip inside
                the inventory, not a separate destination. */}
            <button
              onClick={() => {
                if (windows.baliseManagement.visible && !windows.baliseManagement.minimized) {
                  bringToFront('baliseManagement')
                } else {
                  updateWindow('baliseManagement', { visible: true, minimized: false })
                }
              }}
              className={`relative flex flex-col items-center cursor-pointer group transition-all duration-300 ease-out transform hover:scale-110 w-9 h-9`}
              title={t('balise_management')}
            >
              <div className={`flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10 w-7 h-7 transition-all duration-300 ${windows.baliseManagement.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}`}
                style={{ backgroundColor: '#0891B280', boxShadow: '0 8px 32px #0891B240' }}>
                <Radio size={12} className="text-white" />
              </div>
              {windows.baliseManagement.visible && (
                <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
              )}
            </button>

            {/* Customer Management */}
            <button
              onClick={() => {
                if (windows.customers.visible && !windows.customers.minimized) {
                  bringToFront('customers')
                } else {
                  updateWindow('customers', { visible: true, minimized: false })
                }
              }}
              className={`relative flex flex-col items-center cursor-pointer group transition-all duration-300 ease-out transform hover:scale-110 w-9 h-9`}
              title={t('customer_management')}
            >
              <div className={`flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10 w-7 h-7 transition-all duration-300 ${windows.customers.visible ? 'ring-2 ring-white/50 shadow-xl' : 'hover:shadow-xl'}`}
                style={{ backgroundColor: '#EC489980', boxShadow: '0 8px 32px #EC489940' }}>
                <Building2 size={12} className="text-white" />
              </div>
              {windows.customers.visible && (<div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />)}
            </button>

            {/* Dynamic asset peripheral windows */}
            {(windows.assetSmartLock.visible || windows.assetDashcam.visible || windows.assetObd.visible || windows.assetTags.visible) && (
              <>
                <div className="w-px h-6 bg-white/15 mx-0.5" />
                {windows.assetSmartLock.visible && (
                  <button onClick={() => { if (windows.assetSmartLock.minimized) { updateWindow('assetSmartLock', { visible: true, minimized: false }) } else { bringToFront('assetSmartLock') } }} className="relative flex flex-col items-center cursor-pointer group transition-all duration-300 ease-out transform hover:scale-110 w-9 h-9" title="Smart Lock">
                    <div className={`flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10 w-7 h-7 transition-all duration-300 ${windows.assetSmartLock.minimized ? 'opacity-50' : 'ring-2 ring-white/50 shadow-xl'}`} style={{ backgroundColor: '#F59E0B80' }}>
                      <Lock size={12} className="text-white" />
                    </div>
                    <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
                  </button>
                )}
                {windows.assetDashcam.visible && (
                  <button onClick={() => { if (windows.assetDashcam.minimized) { updateWindow('assetDashcam', { visible: true, minimized: false }) } else { bringToFront('assetDashcam') } }} className="relative flex flex-col items-center cursor-pointer group transition-all duration-300 ease-out transform hover:scale-110 w-9 h-9" title="Dashcam">
                    <div className={`flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10 w-7 h-7 transition-all duration-300 ${windows.assetDashcam.minimized ? 'opacity-50' : 'ring-2 ring-white/50 shadow-xl'}`} style={{ backgroundColor: '#EF444480' }}>
                      <Eye size={12} className="text-white" />
                    </div>
                    <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
                  </button>
                )}
                {windows.assetObd.visible && (
                  <button onClick={() => { if (windows.assetObd.minimized) { updateWindow('assetObd', { visible: true, minimized: false }) } else { bringToFront('assetObd') } }} className="relative flex flex-col items-center cursor-pointer group transition-all duration-300 ease-out transform hover:scale-110 w-9 h-9" title="OBD">
                    <div className={`flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10 w-7 h-7 transition-all duration-300 ${windows.assetObd.minimized ? 'opacity-50' : 'ring-2 ring-white/50 shadow-xl'}`} style={{ backgroundColor: '#8B5CF680' }}>
                      <Cpu size={12} className="text-white" />
                    </div>
                    <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
                  </button>
                )}
                {windows.assetTags.visible && (
                  <button onClick={() => { if (windows.assetTags.minimized) { updateWindow('assetTags', { visible: true, minimized: false }) } else { bringToFront('assetTags') } }} className="relative flex flex-col items-center cursor-pointer group transition-all duration-300 ease-out transform hover:scale-110 w-9 h-9" title="Tags">
                    <div className={`flex items-center justify-center rounded-xl shadow-lg backdrop-blur-sm border border-white/10 w-7 h-7 transition-all duration-300 ${windows.assetTags.minimized ? 'opacity-50' : 'ring-2 ring-white/50 shadow-xl'}`} style={{ backgroundColor: '#06B6D480' }}>
                      <Layers size={12} className="text-white" />
                    </div>
                    <div className="absolute -bottom-1 w-1.5 h-1.5 bg-white rounded-full shadow-lg" />
                  </button>
                )}
              </>
            )}

          </div>

          {/* Auto-Hide Control Arrow - Small arrow in top-right corner */}
          <button
            onClick={() => {
              setTaskbarAutoHide(!taskbarAutoHide)
              console.log(`${!taskbarAutoHide ? '?? Auto-hide ENABLED' : '?? Auto-hide DISABLED'}`)
            }}
            className={`
              absolute -top-2 -right-2 w-4 h-4 rounded-full
              flex items-center justify-center
              transition-all duration-300 hover:scale-125 border border-white/30
              ${taskbarAutoHide 
                ? 'bg-green-500/80 shadow-green-500/50' 
                : 'bg-gray-500/80 shadow-gray-500/50'
              }
            `}
            style={{
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              boxShadow: taskbarAutoHide 
                ? '0 0 12px rgba(34, 197, 94, 0.5)' 
                : '0 0 8px rgba(107, 114, 128, 0.3)'
            }}
            title={taskbarAutoHide ? 'Auto-Hide ON - Taskbar will hide automatically (??)' : 'Auto-Hide OFF - Taskbar stays visible (??)'}
          >
            {taskbarAutoHide ? (
              <ChevronUp size={12} className="text-white" />
            ) : (
              <ChevronDown size={12} className="text-white" />
            )}
          </button>
        </div>

        {/* Dock Reflection Effect */}
        <div 
          className="bg-gradient-to-t from-black/5 to-transparent rounded-b-2xl h-4 -mt-1"
          style={{
            width: '100%',
            transform: 'scaleY(-1)',
            opacity: 0.3
          }}
        />
      </div>

      {/* Show Taskbar Button (when hidden) */}
      {!taskbarVisible && (
        <div 
          className="fixed bottom-2 left-1/2 transform -translate-x-1/2 z-[260]"
          onMouseEnter={() => {
            setTaskbarVisible(true)
            setTaskbarHovered(true)
          }}
        >
          <button
            onClick={() => {
              setTaskbarVisible(true)
              setTaskbarHovered(true)
            }}
            className="bg-black/40 backdrop-blur-xl rounded-full border border-white/20 shadow-xl p-3 text-white hover:bg-black/60 transition-all duration-300 hover:scale-110"
            style={{
              backdropFilter: 'blur(20px) saturate(180%)',
              WebkitBackdropFilter: 'blur(20px) saturate(180%)'
            }}
          >
            <ChevronUp size={16} />
          </button>
        </div>
      )}

      {/* Minimized window icons ? dynamic, covers ALL windows */}
      {Object.entries(windows).some(([, w]) => w.visible && w.minimized) && (
        <div className="fixed bottom-6 left-6 z-[260]">
          <div className={`backdrop-blur-xl rounded-2xl shadow-2xl border px-3 py-3 ${isDarkMode ? 'bg-gray-800/90 border-white/10' : 'bg-white/90 border-white/20'}`}
               style={{
                 backdropFilter: 'blur(20px) saturate(180%)',
                 boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
               }}>
            <div className="flex flex-col items-center space-y-2">
              {Object.entries(windows)
                .filter(([, w]) => w.visible && w.minimized)
                .map(([id]) => {
                  const meta = panelMeta[id as keyof typeof panelMeta]
                  if (!meta) return null
                  return (
                    <button
                      key={id}
                      onClick={() => {
                        updateWindow(id as any, { minimized: false })
                        bringToFront(id)
                      }}
                      className={`group p-3 rounded-xl transition-all duration-200 hover:shadow-lg ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-white/60'}`}
                      title={meta.label}
                    >
                      <div className="w-5 h-5" style={{ color: meta.color }}>
                        {meta.icon}
                      </div>
                    </button>
                  )
                })}
            </div>
          </div>
        </div>
      )}
      </>
      )}

      {/* Available in both workspace modes: an asset may expose more than one
          capability, so the picker must not depend on the Mac window layer. */}
      {picker.visible && (
        <AssetPeripheralPicker
          assetId={picker.assetId}
          peripherals={picker.peripherals}
          onClose={() => setPicker({ visible: false, assetId: '', peripherals: [] })}
          onOpenSingle={(payload) => {
            openAssetPeripheralWindow(payload)
            setPicker({ visible: false, assetId: '', peripherals: [] })
          }}
          onOpenMany={(payloads) => {
            payloads.forEach(openAssetPeripheralWindow)
            setPicker({ visible: false, assetId: '', peripherals: [] })
          }}
        />
      )}

      {/* Mac-style message notifications */}
      <MacMessage
        message={macMessage.message}
        type={macMessage.type}
        isVisible={macMessage.isVisible}
        onClose={() => setMacMessage(prev => ({ ...prev, isVisible: false }))}
      />

      {/* Mac-style confirmation dialogs */}
      <MacConfirm
        message={macConfirm.message}
        isVisible={macConfirm.isVisible}
        onConfirm={macConfirm.onConfirm || (() => {})}
        onCancel={macConfirm.onCancel || (() => {})}
      />

    </div>
  )
}

export default CompleteFleetDashboard

